import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeInSection, SeverityBadge } from '@/components/shared/UiElements';
import { 
  ArrowLeft, Download, Share2, ExternalLink, Scan, AlertTriangle, Zap, 
  Navigation, Eye, UserPlus, MousePointerClick, Accessibility, TrendingUp, 
  Brain, BarChart3, Target, ChevronDown, ChevronUp, Clock, Sparkles,
  CheckCircle2, XCircle, AlertCircle, FileText, Copy, Check, Lock, Crown, ArrowRight,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';
import AuditCheckoutPopup from '@/components/AuditCheckoutPopup';
import { track } from '@/lib/analytics';
import { bandColor, bandLabel, bandTint } from '@/lib/scoreBands';
import { PLAN_PRICES } from '@/config/pricing';
import '@/styles/forms.css';
import ScoreRing from '@/components/ds/ScoreRing';
import StatTile from '@/components/ds/StatTile';
import DimensionBars from '@/components/ds/DimensionBars';
import FindingRow from '@/components/ds/FindingRow';
import PillTabs from '@/components/ds/PillTabs';
import '@/styles/data.css';
import '@/styles/app-states.css';
import {
  VIEWPORTS,
  VIEWPORT_ALL,
  componentLabel,
  absorbedSummary,
  groupByComponent,
  isLocked,
  openFindingCount,
  referenceHeadline,
  matchesViewport,
  viewportCounts,
} from './reportGroups';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dimensions the grounded pipeline can honestly assess from a single
// captured page (see docs/analysis/evidence-contract.md). Onboarding was
// removed — a flow cannot be audited from one page. Older audits that
// stored an onboard_score still render it via the legacy entry below.
//
// A null/undefined score means "not assessed": for the SIX, the card renders
// and says so, because a dimension the product measures and could not score
// this time is information. For a `legacyOnly` entry the card is omitted
// entirely — see the filter at the card grid.
//
// This comment previously said the card "is hidden" on a null score, flatly,
// and nothing hid it. It described the behaviour someone intended rather than
// the behaviour that shipped, which is how "Onboarding — Not assessed" reached
// every delivered report.
const dimMeta = {
  navigation: { label: 'Navigation', desc: 'The navigation visible on this page', icon: Navigation, key: 'nav_score', color: 'var(--accent-text)' },
  visual_hierarchy: { label: 'Visual Hierarchy', desc: 'Layout clarity and information flow', icon: Eye, key: 'visual_score', color: '#8B5CF6' },
  onboarding: { label: 'Onboarding', desc: 'First-time user experience quality', icon: UserPlus, key: 'onboard_score', color: '#6B7280', legacyOnly: true },
  friction_points: { label: 'Friction Points', desc: 'Friction visible on this page', icon: MousePointerClick, key: 'friction_score', color: 'var(--severity-major-text)' },
  accessibility: { label: 'Accessibility', desc: 'Measured contrast, alt text, structure', icon: Accessibility, key: 'a11y_score', color: 'var(--success)' },
  conversion_design: { label: 'Conversion Design', desc: 'How well this page drives action', icon: TrendingUp, key: 'conv_score', color: '#EC4899' },
  mobile_ux: { label: 'Mobile UX', desc: 'From the 390px mobile capture', icon: MousePointerClick, key: 'mobile_score', color: 'var(--ink-1)' },
};

// Dimension justifications shown to FREE users: a genuine sample (Navigation +
// Visual Hierarchy) that proves the site was assessed. The rest — Friction
// Points, Accessibility, Conversion Design, and Mobile UX — are part of the
// paid report. Scores + bars still show for every dimension; only the analysis
// paragraph is gated. Paid users see all of them.
const FREE_DIMENSION_JUSTIFICATIONS = ['navigation', 'visual_hierarchy'];

// The SIX, mapped from DimensionBars' card key to the audit field that holds the
// score. `dimMeta` above still carries a seventh entry, `onboarding`, marked
// legacyOnly — it exists so a PRE-2026-07-15 audit's stored onboard_score is not
// silently dropped from a historical report, and it is deliberately NOT in this
// list: the bars are what the product measures TODAY, and that is six.
const DIM_KEY_BY_CARD = [
  ['navigation', 'nav_score'],
  ['visual_hierarchy', 'visual_score'],
  ['friction_points', 'friction_score'],
  ['accessibility', 'a11y_score'],
  ['conversion_design', 'conv_score'],
  ['mobile_ux', 'mobile_score'],
];

const regionPositions = {
  'top-left': { top: '15%', left: '15%' },
  'top-right': { top: '15%', left: '80%' },
  'center': { top: '50%', left: '50%' },
  'bottom-left': { top: '75%', left: '15%' },
  'bottom-right': { top: '75%', left: '80%' },
  'full-width': { top: '50%', left: '50%' },
};

// Helper to get annotation position (supports both new annotation format and old region format)
const getAnnotationPosition = (finding, index) => {
  // New annotation format: { x: 0-100, y: 0-100, ui_element: "..." }
  if (finding.annotation && typeof finding.annotation.x === 'number' && typeof finding.annotation.y === 'number') {
    return {
      top: `${Math.max(5, Math.min(95, finding.annotation.y))}%`,
      left: `${Math.max(5, Math.min(95, finding.annotation.x))}%`,
      uiElement: finding.annotation.ui_element || ''
    };
  }
  // Old region format: fallback
  const pos = regionPositions[finding.region] || regionPositions['center'];
  // Add slight offset to prevent overlapping
  const offset = {
    top: `calc(${pos.top} + ${(index % 2) * 8}%)`,
    left: `calc(${pos.left} + ${(index % 3 - 1) * 12}%)`
  };
  return { ...offset, uiElement: '' };
};

// Score colour, label and wash all come from the ONE band table
// (frontend/src/lib/scoreBands.js, mirroring backend/audit/scoring.py). These
// previously used three uncoordinated ladders — colour switched at 70/50 while
// the label switched at 80/60/40, so a 75 rendered green but read "Good".
const getScoreColor = (s) => bandColor(s);
const getScoreLabel = (s) => bandLabel(s);
/* ═══ A TINT NEEDS A GROUND UNDER IT ════════════════════════════════════════
 *
 * The page background is a three-layer stack (--page-bg): an indigo wash, an
 * orange wash, and a 28px dot grid. Every card on this page was painted with a
 * BARE rgba() at 0.1 alpha, so all three layers showed straight through the
 * card and the dots read as sitting ON TOP of the box.
 *
 * The tell was that only some boxes looked wrong: `minor` findings use
 * var(--surface-white), which is opaque, and those were clean. Critical and
 * major findings, and every dimension card, were 10% washes over the page.
 *
 * > A translucent fill is not a background. It is a filter over whatever
 * > happens to be behind it, and on this page what is behind it is texture.
 *
 * opaque() layers the tint over --surface-white so the card carries its own
 * ground. Two background layers, no extra element, no z-index games - z-index
 * would not have helped anyway, since nothing was painting out of order. The
 * card was simply see-through.
 *
 * NOT applied to chips, icon circles or badges: those sit INSIDE a card that is
 * already opaque, so their translucency composites over the card exactly as
 * intended. Only surfaces that touch the page background need this.
 */
const opaque = (tint) => `linear-gradient(0deg, ${tint}, ${tint}), var(--surface-white)`;

const getScoreBgGradient = (s) => opaque(bandTint(s, 0.1));

// PHASE G. `PremiumScoreGauge` lived here: 50 lines of inline SVG with a glow
// ring, an inner highlight ring and its own dash arithmetic. It is
// components/ds/ScoreRing.jsx now, for one reason that matters more than tidy
// components — THE PDF DREW A DIFFERENT SHAPE. The web ring was a full circle
// and the PDF's was a 180-degree semicircle gauge, so "the score ring" was two
// components that had never matched. One implementation, one geometry, emitted
// on both sides. See ScoreRing.jsx and _build_pdf_html.

// Mini Radial Score for Dimensions
const MiniRadialScore = ({ score, size = 56, color }) => {
  const radius = size * 0.38;
  const strokeWidth = size * 0.12;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const displayColor = color || getScoreColor(score);
  
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="var(--surface-panel)" strokeWidth={strokeWidth} />
        <motion.circle
          cx={size/2} cy={size/2} r={radius} fill="none" stroke={displayColor} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      </svg>
      <span className="absolute text-xs font-bold" style={{ color: displayColor }}>{score}</span>
    </div>
  );
};

// Finding Card Component - Dark Theme
/* ── MEASURED DELTA (report-redesign Phase 2b) ─────────────────────────
   "3.95:1, needs 4.5:1, 12% short" in place of "fails WCAG AA".

   THE SERVER DECIDES WHETHER THIS RENDERS, NOT THIS COMPONENT. `finding.delta`
   is present only when audit/deltas.bar_for allowed it, which means: the
   evidence came from our own measurement layer, the quote carried exactly ONE
   value (so the number shown is necessarily the one the validator checked),
   and a real published threshold exists for it. Three families qualify today
   and two of them reach here; everything else renders with no bar at all,
   which is the correct output and not a gap to fill.

   Do NOT add a fallback that draws a bar from a percentage, a score, or a
   severity. A bar in this slot reads as a measurement, and the one outcome
   worse than no bar is a bar with a number we invented behind it. */
const DeltaBar = ({ delta }) => {
  if (!delta) return null;
  // The track is scaled to the THRESHOLD, so the filled portion reads as
  // "how much of the bar was cleared". Clamped only at the top: a measured
  // value above its threshold would not be a finding.
  const pct = Math.max(4, Math.min(100, (delta.measured / delta.threshold) * 100));
  return (
    <div className="mt-2" data-testid="delta-bar">
      <div className="flex items-baseline gap-1.5 flex-wrap">
        <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--ink-1)' }}>
          {delta.label}
        </span>
      </div>
      <div
        className="mt-1 h-1.5 rounded-full overflow-hidden"
        style={{ background: 'var(--surface-sand-deep)', border: '1px solid var(--border-2)' }}
        role="img"
        aria-label={`Measured ${delta.measured}${delta.unit === 'px' ? ' pixels' : ' to 1'}, threshold ${delta.threshold}${delta.unit === 'px' ? ' pixels' : ' to 1'}, ${delta.shortfall_pct}% short`}
      >
        {/* --accent is a FILL. This is one. */}
        <div className="h-full" style={{ width: `${pct}%`, background: 'var(--accent)' }} />
      </div>
      <p className="text-[10px] mt-1" style={{ color: 'var(--ink-3)' }}>{delta.standard}</p>
    </div>
  );
};

/* ── LEVERAGE (report-redesign Phase 2a) ───────────────────────────────
   "3 groups hold 24 of 31 findings", then the groups themselves.

   THE WORDING IS A RULING, NOT A STYLE CHOICE. It says "hold", never
   "close" or "fix". No field on a finding distinguishes two defects of
   different kinds in the same component and dimension, so nothing here
   counts changes, and a headline that said so would be an unsourced claim.
   audit/leverage.py carries FIX_CLAIM_IS_UNSOURCED with the argument.

   THE GROUP COUNTS SUM TO MORE THAN THE FINDING COUNT, ON PURPOSE. A footer
   CTA is genuinely both `footer` and `conversion_path` and appears under
   both. That is why the headline number comes from the server as a set
   union over distinct findings and is NOT computed here by adding the
   counts up. Adding them up is the defect this note exists to prevent. */
const LeverageLine = ({ leverage }) => {
  if (!leverage || !leverage.groups?.length) return null;
  const pretty = (label) =>
    label.split(' / ').map(part => part.replace(/_/g, ' ')).join(' · ');
  return (
    <div
      className="rounded-xl p-5 mb-5"
      style={{ background: 'var(--surface-panel)', border: '1px solid var(--border-1)' }}
      data-testid="leverage-line"
    >
      <p className="font-heading text-base" style={{ color: 'var(--ink-1)' }}>{leverage.text}</p>
      <p className="text-xs mt-1" style={{ color: 'var(--ink-2)' }}>
        Grouped by the part of the page and the dimension. A finding that spans
        two parts appears under both, so these counts add up to more than the
        finding total.
      </p>
      <ul className="mt-3 flex flex-wrap gap-2">
        {leverage.groups.map((g) => (
          <li
            key={g.label}
            className="text-[11px] px-2.5 py-1 rounded-full"
            style={{ background: 'var(--surface-white)', border: '1px solid var(--border-2)', color: 'var(--ink-1)' }}
          >
            <span className="capitalize">{pretty(g.label)}</span>
            <span className="ml-1.5 tabular-nums font-semibold" style={{ color: 'var(--accent-text)' }}>{g.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ── VIEWPORT FILTER (redesign Phase 3a) ───────────────────────────────
   A FILTER WITH COUNTS, NOT A SECTION. Splitting the report into a Desktop
   half and a Mobile half would put the same component in two places and make
   a designer read it twice; a filter leaves one spine and narrows it.

   THREE CHIPS. The brief asked for a fourth, "Both", and there is nothing to
   put in it: `is_mobile` is a boolean written by the server from
   report_format.is_mobile_finding, so Desktop and Mobile partition the set
   and Both would read 0 on every report. See reportGroups.js for the full
   note and for what would have to change before it could come back. */
const ViewportFilter = ({ counts, value, onChange }) => {
  /* PillTabs, not a hand-rolled chip row. The first cut of this was three
     buttons with an ink fill on the active one, and the filled-control guard
     caught it: an ink-filled button outside the three approved skins reads as
     a primary action, and this is a filter. Reusing the design-system
     component also brings the things a hand-rolled row silently omits -
     roving tabindex, arrow-key navigation, :focus-visible, and the ::after
     that gives a 30px pill a 44px target. */
  const index = Math.max(0, VIEWPORTS.findIndex((v) => v.key === value));
  return (
    <div data-testid="viewport-filter">
      <PillTabs
        items={VIEWPORTS.map(({ key, label }) => `${label} ${counts[key] ?? 0}`)}
        value={index}
        onChange={(i) => onChange(VIEWPORTS[i].key)}
        tone="sand"
        size="sm"
        ariaLabel="Filter findings by viewport"
      />
    </div>
  );
};

/* ── CROSS-PAGE, NAMED EVEN WHEN EMPTY (redesign Phase 3a) ─────────────
   It used to render nothing at all when the list was empty, on the argument
   that a header over an empty list reads as a failure. That argument was
   wrong for the commonest case: a FREE audit is capped at one page
   (page_set.MAX_PAGES_FREE), so the section is empty for a reason the reader
   would want to know, and hiding it hides the reason along with it.

   So it is always named. When empty it says what it needs rather than
   apologising, which is the one honest thing to put there. */
const CrossPageEmpty = ({ pageCount }) => (
  <div
    className="rounded-2xl p-5"
    style={{ background: 'var(--surface-panel)', border: '1px dashed var(--border-2)' }}
    data-testid="cross-page-empty"
  >
    <p className="text-sm" style={{ color: 'var(--ink-1)' }}>
      Nothing to compare yet. This section needs at least two audited pages;
      this audit covers {pageCount === 1 ? 'one page' : `${pageCount} pages`}.
    </p>
  </div>
);

const FindingCard = ({ finding, index, isExpanded, onToggle }) => {
  const sevStyles = {
    // opaque(): these sit on the page background, whose dot grid was showing
    // through the 10% wash. `minor` was always var(--surface-white) and never
    // had the problem, which is what made the defect look intermittent.
    critical: { border: 'var(--danger)', bg: opaque('rgba(239,68,68,0.1)'), icon: XCircle, iconColor: 'var(--danger)' },
    major: { border: 'var(--severity-major-text)', bg: opaque('rgba(245,158,11,0.1)'), icon: AlertCircle, iconColor: 'var(--severity-major-text)' },
    minor: { border: 'var(--border-2)', bg: 'var(--surface-white)', icon: AlertCircle, iconColor: 'var(--ink-2)' },
  };
  const style = sevStyles[finding.severity] || sevStyles.minor;
  const Icon = style.icon;
  
  const effortStyles = {
    low: { label: 'Quick Fix', bg: 'rgba(16,185,129,0.15)', text: 'var(--success)', border: 'rgba(16,185,129,0.3)' },
    medium: { label: 'Moderate', bg: 'rgba(245,158,11,0.15)', text: 'var(--severity-major-text)', border: 'rgba(245,158,11,0.3)' },
    high: { label: 'Major Effort', bg: 'rgba(239,68,68,0.15)', text: 'var(--danger)', border: 'rgba(239,68,68,0.3)' },
  };
  const effort = effortStyles[finding.effort] || effortStyles.medium;
  
  const impactStyles = {
    high: { label: 'High Impact', bg: 'rgba(16,185,129,0.15)', text: 'var(--success)' },
    medium: { label: 'Medium Impact', bg: 'rgba(74,158,255,0.15)', text: '#4A9EFF' },
    low: { label: 'Low Impact', bg: 'var(--surface-sand-deep)', text: 'var(--ink-2)' },
  };
  const impact = impactStyles[finding.impact] || impactStyles.medium;
  
  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 10 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ delay: 0.1 + index * 0.03 }}
      className="rounded-xl overflow-hidden"
      style={{ 
        background: style.bg, 
        border: '1px solid var(--border-1)',
        borderLeftWidth: '4px',
        borderLeftColor: style.border
      }}
      data-testid={`finding-${index}`}
    >
      <div 
        className="p-5 cursor-pointer transition-colors"
        onClick={onToggle}
        style={{ ':hover': { background: 'rgba(255,255,255,0.03)' } }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <div 
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: 'var(--accent-text)' }}
            >
              <span className="text-[11px] font-bold text-white">{index + 1}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <SeverityBadge severity={finding.severity} />
                {/* WHICH SCREEN. Present only on a multi-screen capture, and
                    only when the validator could match it against the
                    capture's own labels - so its absence means "one surface"
                    or "could not be verified", never "we did not bother". */}
                {finding.screen && (
                  <span className="uxa-screen-tag" data-testid="finding-screen">{finding.screen}</span>
                )}
                <span className="text-[10px] capitalize" style={{ color: 'var(--ink-2)' }}>{finding.dimension?.replace('_', ' ')}</span>
                {/* WHICH PART OF THE PAGE. Derived on the server from the
                    finding's own locator, never stored, and a LIST because a
                    footer CTA is genuinely both. `other` means the mapper
                    missed it, so it is not shown - a bucket that means "we
                    could not tell" is noise on a card. */}
                {(finding.components || []).filter(c => c !== 'other').map(c => (
                  <span
                    key={c}
                    className="text-[10px] px-2 py-0.5 rounded-full capitalize"
                    style={{ background: 'var(--surface-sand-deep)', color: 'var(--ink-2)' }}
                    data-testid="component-tag"
                  >
                    {c.replace(/_/g, ' ')}
                  </span>
                ))}
                {/* Mobile tag (Track 6, Phase 5): mobile-specific findings are
                    labelled so a reader knows this is a 390px-viewport issue,
                    not a desktop one. Brand orange, never blue. */}
                {finding.is_mobile && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide" style={{ background: 'var(--accent-tint)', color: 'var(--accent-text)' }} data-testid="mobile-tag">
                    Mobile
                  </span>
                )}
                {finding.annotation?.ui_element && ['critical', 'major'].includes(finding.severity) && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(28, 28, 40, 0.05)', color: 'var(--ink-2)' }}>
                    {finding.annotation.ui_element.length > 25 
                      ? finding.annotation.ui_element.substring(0, 25) + '...' 
                      : finding.annotation.ui_element}
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-sm leading-snug" style={{ color: 'var(--ink-1)' }}>{finding.title}</h3>
              {/* The measured value sits with the title, collapsed or not: it
                  is the evidence that the finding was measured rather than
                  observed, and burying it behind a click is what "fails WCAG
                  AA" used to do. Absent on most findings by design. */}
              <DeltaBar delta={finding.delta} />
              {!isExpanded && (
                <p className="text-xs mt-1 line-clamp-1" style={{ color: 'var(--ink-2)' }}>{finding.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span 
              className="hidden sm:inline-flex text-[10px] px-2 py-0.5 rounded-full font-medium"
              style={{ background: effort.bg, color: effort.text, border: `1px solid ${effort.border}` }}
            >
              {effort.label}
            </span>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
            </motion.div>
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-0">
              <div className="pl-10">
                <p className="uxa-dense mb-4" style={{ color: 'var(--ink-2)' }}>{finding.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span 
                    className="text-[10px] px-2.5 py-1 rounded-full font-medium flex items-center gap-1"
                    style={{ background: effort.bg, color: effort.text }}
                  >
                    <Clock className="w-3 h-3" /> {effort.label}
                  </span>
                  <span 
                    className="text-[10px] px-2.5 py-1 rounded-full font-medium flex items-center gap-1"
                    style={{ background: impact.bg, color: impact.text }}
                  >
                    <Sparkles className="w-3 h-3" /> {impact.label}
                  </span>
                  {finding.region && (
                    <span 
                      className="text-[10px] px-2.5 py-1 rounded-full font-medium"
                      style={{ background: 'rgba(100,116,139,0.15)', color: 'var(--ink-2)' }}
                    >
                      Region: {finding.region?.replace('-', ' ')}
                    </span>
                  )}
                </div>
                
                {/* Evidence — the thing itself. This block is what separates
                    this report from generic checklist output: the exact
                    on-page text, measured value, or element the finding
                    stands on. */}
                {finding.evidence?.quote && (
                  <div
                    className="rounded-xl p-4 mb-3"
                    style={{ background: 'rgba(28,28,40,0.04)', border: '1px solid rgba(28,28,40,0.10)' }}
                    data-testid={`finding-evidence-${index}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Scan className="w-4 h-4" style={{ color: 'var(--ink-1)' }} />
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-1)' }}>
                        Evidence{finding.evidence.source === 'measurement' ? ' · measured' : ''}
                      </span>
                    </div>
                    <p className="uxa-dense font-mono" style={{ color: 'var(--ink-2)' }}>
                      “{finding.evidence.quote}”
                    </p>
                    {finding.evidence.location && (
                      <p className="text-[10px] mt-1.5" style={{ color: 'var(--ink-2)' }}>Location: {finding.evidence.location}</p>
                    )}
                  </div>
                )}

                {finding.user_impact && (
                  <p className="uxa-dense mb-4" style={{ color: 'var(--ink-2)' }}>
                    <span className="font-semibold" style={{ color: 'var(--ink-1)' }}>Why it matters: </span>
                    {finding.user_impact}
                  </p>
                )}

                {finding.recommendation && (
                  <div
                    className="rounded-xl p-4"
                    style={{ background: 'var(--accent-tint)', border: '1px solid var(--border-2)' }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--accent-text)' }} />
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--accent-text)' }}>Recommendation</span>
                    </div>
                    <p className="uxa-dense" style={{ color: 'var(--ink-2)' }}>{finding.recommendation}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* Locked finding — the 04 / LOCKED artboard of the App States design,
 * reduced on 2026-09-03.
 *
 * A locked finding KEEPS its title, its severity and its dimension, and loses
 * its evidence, its effort estimate and its recommended fix. That is what the
 * server actually sends: locked findings are reduced to a teaser before
 * serialisation, so there is no withheld text in the payload to reveal.
 *
 * ── WHY IT IS A ROW NOW AND NOT A CARD ────────────────────────────────────
 *
 * Measured on a real free payload with seven locked findings: each row was
 * 333px on desktop and 386px on mobile, so the locked set alone was 2331px
 * (2.6 screens, 29% of the page) and 2610px (3.1 screens, 22%). The page was
 * 9.0 screens and 14.3 screens.
 *
 * Three things were paying for that and none of them earned it:
 *
 *   SEVEN CTAs   One per row. The comment that used to sit here already
 *                half-admitted the problem - it shrank the button to
 *                --compact "because this button REPEATS" and "competes with
 *                the upgrade banner, which is the one action on the page
 *                that should be loudest". The honest version of that
 *                reasoning is not a smaller button seven times. The ROW is
 *                the target now, so the action survives with no chrome and
 *                no height, and the banner carries the only visible CTA.
 *   21 TAGS      "Evidence locked", "Effort estimate locked", "Recommended
 *                fix locked" is three phrasings of one fact, three times per
 *                row. One marker says it once.
 *   SKELETON BARS  Three placeholder rules standing in for sentences that
 *                were never sent. They imitate content that does not exist.
 *                Once the row is one line, its brevity IS the signal.
 *
 * ── WHAT DID NOT CHANGE, AND MUST NOT ─────────────────────────────────────
 *
 * `data-paywall-marker` stays on the row: the paywall_hit telemetry observes
 * it with an IntersectionObserver, and moving it would silently stop that
 * event rather than break anything visible.
 *
 * The severity chip stays `FindingRow`, which carries the three-step meter.
 * Severity never depends on colour alone, and a second chip implementation
 * here would be a second chance to drop the meter.
 *
 * The previous version used `backdrop-filter: blur(4px)` over the real title.
 * backdrop-filter is banned by name in tokens.css and CLAUDE.md (it forms its
 * own stacking context over transform-animated cards - the G1 defect), and a
 * CSS filter is never the paywall mechanism (docs/DECISIONS.md). Still gone.
 */
const LockedContent = ({ title, severity, dimension, onUnlock }) => {
  const level = severity === 'critical' ? 'critical' : severity === 'major' ? 'major' : 'minor';
  return (
    <div className="uxa-finding uxa-finding--locked-row" data-paywall-marker>
      {/* The whole row is the target, so there is no button on it. 44px
          minimum is met by the row itself, which is taller than that with
          the chip inside. */}
      <FindingRow
        severity={level}
        title={title}
        dimension={dimension?.replace(/_/g, ' ')}
        /* onUnlock already fires upgrade_clicked at the call site, so this
           does NOT track. Two events per click would double the count on the
           one CTA whose rate the paywall work is judged by. */
        onClick={onUnlock}
      />
      <span className="uxa-finding-tag uxa-finding-tag--locked uxa-finding-tag--single">
        Evidence, effort and fix locked
      </span>
    </div>
  );
};

/* A finding belongs to every component it touches, and renders in exactly
 * ONE of them. This is what the others show instead.
 *
 * WHY IT CARRIES SO LITTLE. The row must leak nothing whether the finding it
 * points at is LOCKED or OPEN, and the cheapest way to guarantee that is to
 * give it no field that could leak. It takes a headline and a destination.
 * There is no `finding` prop, so there is no evidence, no delta, no
 * description and no recommendation to forget to strip - a future edit
 * cannot reintroduce them without changing this signature, which is the
 * point. `referenceHeadline` (reportGroups.js) applies the teaser rule to
 * the one string that does get through, with no branch on locked.
 *
 * Severity is deliberately absent too, and not for leak reasons: a severity
 * badge here would carry the three-step meter and read as a finding card,
 * which is the visual weight this row exists to avoid. Severity is on the
 * card, one click away, where the reader is going.
 */
const FindingReference = ({ headline, destinationKey, destinationLabel }) => (
  <a
    href={`#component-group-${destinationKey}`}
    className="uxa-finding-reference"
    data-testid="finding-reference"
  >
    <span className="uxa-finding-reference-title">{headline}</span>
    <span className="uxa-finding-reference-where">
      Shown under {destinationLabel}
      <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
    </span>
  </a>
);

// Upgrade banner — the 05 / UPGRADE artboard. Dark panel, 72px rule grid, four
// benefits, price card. Every line of copy is the copy that was already here;
// the lowCount and criticalCount branches are unchanged.
/* `openCount` and `total` are COUNTED by the caller (openFindingCount and
 * findings.length), not derived from each other. The previous signature took
 * `lockedCount` and printed `lockedCount + 1`, which is findings.length
 * written the long way round: it asserted "1 of N" while never looking at
 * how many findings were open. It read true only because the free trim
 * happens to leave exactly one. */
const UpgradeBanner = ({ openCount, total, criticalCount, price = PLAN_PRICES.STARTER.usd, lowCount = false, onUpgrade }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="uxa-upgrade"
  >
    <div className="uxa-upgrade-grid">
      <div>
        <div className="uxa-upgrade-eyebrow">Complete report</div>
        {lowCount ? (
          <>
            <h3 className="uxa-upgrade-title">This page is largely solid</h3>
            <p style={{ fontSize: '16px', lineHeight: 1.6, color: 'var(--ink-4)', margin: '12px 0 0', maxWidth: '520px' }}>
              We report only findings backed by evidence, so a short list means fewer real problems, not a thin report. The full report adds the rest with evidence and fixes.
            </p>
          </>
        ) : (
          <>
            {/* The count as an ATTRIBUTE as well as in the sentence, so a render
                harness can assert it with a selector. The banner claimed
                "1 of N" for months by printing lockedCount + 1, and nothing
                could see the claim because it lived in a sentence. The
                proof is a route in check-routes-render.mjs. */}
            <h3
              className="uxa-upgrade-title"
              data-testid="upgrade-headline"
              data-open-count={openCount}
            >
              You are seeing {openCount} of {total} verified findings
            </h3>
            <p style={{ fontSize: '16px', lineHeight: 1.6, color: 'var(--ink-4)', margin: '12px 0 0', maxWidth: '520px' }}>
              {criticalCount > 0 ? (
                <>Every one cites its evidence like the finding above. <span style={{ fontWeight: 700, color: 'var(--accent-soft)' }}>{criticalCount} Critical</span> in total.</>
              ) : (
                <>Every one cites its evidence like the finding above.</>
              )}
            </p>
          </>
        )}
        <div className="uxa-upgrade-benefits">
          {[
            ['Full evidence per finding', 'What we measured, where, and the research behind it'],
            ['Annotated screenshots', 'Every problem area marked on the real capture'],
            ['Prioritised roadmap', 'Sprint order with hour-level effort estimates'],
            ['PDF export', 'Share-ready for stakeholders and clients'],
          ].map(([t, note]) => (
            <div key={t} className="flex gap-3 items-start">
              <span className="uxa-upgrade-tick" aria-hidden="true">{'\u2713'}</span>
              <div>
                <div className="uxa-upgrade-benefit-title">{t}</div>
                <div className="uxa-upgrade-benefit-note">{note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="uxa-upgrade-price">
        <div className="flex items-baseline gap-2 justify-center">
          <span className="uxa-upgrade-price-figure">${price}</span>
          <span style={{ fontSize: 'var(--type-dense-size)', fontWeight: 500, color: 'var(--ink-4)' }}>per audit</span>
        </div>
        <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--ink-4)', marginTop: '6px' }}>This audit, keep forever</div>
        {/* A button, not a Link. It opens the checkout popup on this page
            rather than navigating to a plans page - the customer is already
            looking at the report they want unlocked. */}
        <button
          type="button"
          data-testid="upgrade-banner-cta"
          onClick={() => {
            track('upgrade_clicked', { source: 'report_blur', cta: lowCount ? 'upgrade_banner_lowcount' : 'upgrade_banner' });
            onUpgrade?.();
          }}
          className="uxa-upgrade-cta"
        >
          {/* THE PRICE IS ALREADY IN THIS PANEL, at 44px, three lines above.
              The label read "Get the full report, $49" directly beneath a
              great big $49, so the panel said the number twice and the button
              had to carry a comma to fit it in.

              A button label is for the ACTION. The figure states the price,
              "per audit" and "This audit, keep forever" qualify it, and the
              button says what pressing it does. Nothing is hidden: the price
              is larger and more legible than it was inside the label. */}
          {lowCount ? 'See the full report' : 'Unlock the full report'}
          <span className="uxa-cta-knob" style={{ width: '36px', height: '36px' }} aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
        </button>
      </div>
    </div>
  </motion.div>
);

// Partial Score Reveal Component - Dark Theme
// Honest upsell (Track 5): the previous version invented "+8 points" and a
// projected "{score+8}+" — the exact fabricated-projection class Track 2/4
// deleted from the reports, still alive here in the web free-tier upsell. No
// number was measured that says a fix is worth 8 points, so no such number
// ships. The nudge points at the lowest-scoring dimension (a real, measured
// score) and offers its concrete findings — no invented arithmetic.
const PartialScoreReveal = ({ lowestDim, lowestScore, onUnlock }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="rounded-2xl p-6"
      style={{
        background: opaque('rgba(245,158,11,0.1)'),
        border: '1px solid rgba(245,158,11,0.25)'
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(245,158,11,0.2)' }}
        >
          <TrendingUp className="w-6 h-6" style={{ color: 'var(--severity-major-text)' }} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold mb-2" style={{ color: 'var(--ink-1)' }}>Your lowest scoring area is {lowestDim} ({lowestScore}/100)</h3>
          <p className="text-sm mb-4" style={{ color: 'var(--ink-2)' }}>
            The full report opens every {lowestDim.toLowerCase()} finding with its measured
            evidence and the exact fix, alongside the rest of the audit.
          </p>
          {/* PHASE F. This was filled with --severity-major-text. A severity
              colour on an ACTION says "this button is a Major issue", which is
              not what it is — and it is the one fill in the product that carried
              meaning it was not entitled to borrow. Primary skin. */}
          <button onClick={onUnlock} className="uxa-submit uxa-submit--auto">
            See the {lowestDim} findings
            <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

/* ── ONE SHAPE FOR BOTH ERAS ───────────────────────────────────────────────
   Screenshots arrive one of two ways and the difference is not this page's
   business. An audit whose images live in R2 sends signed URLs; an audit from
   before the migration, or one whose externalize failed, sends base64 as it
   always did. Both become a string that can go straight into <img src>, so
   every consumer below reads one thing.

   The API sends URLs in `screenshot_urls` and bytes in `screenshots`, never
   both, and prefers URLs when it has them. */
const toSrc = (v) =>
  (typeof v === 'string' && /^(https?:|data:)/.test(v)) ? v : `data:image/jpeg;base64,${v}`;

/* The partial-capture sentence, mirroring backend multi_page.coverage_note_of.
   Prefers the stored field; falls back to composing it, so an audit written
   before `coverage_note` existed still discloses rather than going quiet.
   Empty string when nothing is missing, so the caller renders unconditionally. */
/* "https://example.com/pricing" -> "/pricing", and "https://example.com" -> "/".
   The chips sit three-to-a-row on a phone; a full URL wraps to two lines each
   and the finding's own sentence stops being the thing you read first. The
   full URL stays in the title attribute for anyone who needs it. */
/* The model's dimension keys, in the reader's words. Six, never seven — the
   removed Onboarding dimension is absent here as everywhere else. */
const DIMENSION_LABELS = {
  navigation: 'Navigation',
  visualHierarchy: 'Visual Hierarchy',
  frictionPoints: 'Friction Points',
  accessibility: 'Accessibility',
  conversionDesign: 'Conversion Design',
  mobileUX: 'Mobile UX',
};

const shortPath = (url) => {
  try {
    const u = new URL(url);
    return (u.pathname === '/' ? '/' : u.pathname.replace(/\/$/, '')) + (u.search || '');
  } catch {
    return url;
  }
};

const coverageNoteOf = (audit) => {
  if (!audit) return '';
  if (typeof audit.coverage_note === 'string') return audit.coverage_note;
  const requested = audit.pages_requested || [];
  const captured = audit.pages_captured || [];
  if (!requested.length) return '';
  const missing = requested.filter((u) => !captured.includes(u));
  if (!missing.length) return '';
  if (!captured.length) return 'None of the requested pages could be captured.';
  const noun = captured.length === 1 ? 'page' : 'pages';
  return `This audit covers ${captured.length} of the ${requested.length} pages requested. `
    + `Scores and findings below are measured on the ${captured.length} ${noun} we could capture. `
    + `Not captured: ${missing.join(', ')}.`;
};

// Mirrors backend/audit/size_guard.describe_drops. Empty means "nothing to
// disclose", so the block below can render unconditionally and its presence is
// the signal — the same contract coverageNoteOf has.
//
// Silent about the payloads a reader would never have seen. The guard drops
// duplicates and full-page captures first, and neither is rendered anywhere, so
// naming them would report a loss the customer did not suffer.
// Mirrors backend/audit/capture_coverage.describe(). "" means nothing to
// disclose, so callers render it unconditionally and its presence is the signal.
//
// A page analysed from part of its height IS scored — it was assessed, just
// incompletely — but the score never renders without this sentence. That is the
// same rule as the page-1 score label, and the reason both live in accessors
// rather than in the markup.
const COVERAGE_COMPLETE = 0.995;
const captureNoteOf = (page) => {
  const rec = page && page.capture_coverage;
  if (!rec || !rec.total_px) return '';
  const fraction = Math.min(1, Math.max(0, (rec.seen_px || 0) / rec.total_px));
  if (fraction >= COVERAGE_COMPLETE) return '';
  const unseen = Math.max(0, rec.total_px - (rec.seen_px || 0));
  return `This page was analysed from the top ${Math.round(fraction * 100)}% of its `
    + `full height; the remaining ${unseen.toLocaleString()}px was not sent to the `
    + 'analyser. Its score and findings describe the part that was seen.';
};

const storageNoteOf = (audit) => {
  const dropped = ((audit && audit.size_guard) || {}).dropped || [];
  const visible = dropped.filter((d) => d && d.visible);
  if (!visible.length) return '';
  const reasons = Array.from(new Set(visible.map((d) => d.reason || ''))).sort().join(', ');
  return `This audit was unusually large, so some screenshots were not stored: ${reasons}. `
    + 'The analysis was run on the full set of images; only the saved copies were reduced.';
};

const toSrcList = (urls, b64s) =>
  ((urls && urls.length ? urls : (b64s || [])).filter(Boolean).map(toSrc));

export default function AuditReportPage() {
  const { auditId } = useParams();
  // ADMIN PREVIEW: `?as=customer` is forwarded to every report request, not
  // just the first. The server decides what it means (server.py,
  // admin_is_previewing_as_customer); this only carries it. All four requests
  // must agree - a preview that trims the JSON while the screenshot endpoint
  // still signs sharp URLs would show a paywalled report with unpaywalled
  // images, which is a worse lie than not having the preview at all.
  const { search } = useLocation();
  const asCustomer = new URLSearchParams(search).get('as') === 'customer';
  const viewQuery = asCustomer ? '?as=customer' : '';
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  //: The 390px capture. Collected on every URL audit since mobile scoring was
  //: added and never sent to a browser until 2026-08-20 - the screenshots
  //: endpoint did not project it.
  const [screenshotsMobile, setScreenshotsMobile] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [expandedFindings, setExpandedFindings] = useState({});
  const [copiedLink, setCopiedLink] = useState(false);

  /* ── SIGNED URLs EXPIRE, SO ONE RETRY WHEN ONE DOES ─────────────────────
     A signed screenshot URL is good for 15 minutes. That covers a whole report
     session — load, scroll, zoom, download the PDF — but it does not cover a
     tab left open over lunch, where the browser evicts an image and re-fetches
     it against a dead signature. The customer sees a broken screenshot on a
     report they paid for.

     So: on an image error, ask the endpoint for a fresh set once. Entitlement
     is re-checked server-side on that call, exactly as on the first one, so
     this cannot upgrade anyone — an unentitled viewer gets fresh BLURRED URLs.

     ONCE, not on a loop. A genuinely missing object fails every time, and an
     onError that refetches forever turns one absent image into a request storm
     against our own API. The ref latches; a page reload is the way out, which
     is what a customer does anyway.

     Inline (pre-migration) audits never reach this: their src is a data URI,
     which cannot expire and cannot 403. */
  const refreshedOnce = useRef(false);
  const refreshScreenshots = useCallback(async () => {
    if (refreshedOnce.current) return;
    refreshedOnce.current = true;
    try {
      const r = await axios.get(`${API}/audits/${auditId}/screenshots${viewQuery}`, { withCredentials: true });
      setScreenshots(toSrcList(r.data.screenshot_urls, r.data.screenshots));
      setScreenshotsMobile(toSrcList(r.data.screenshot_mobile_urls, r.data.screenshots_mobile));
    } catch {
      /* Leave what is on screen. A failed refresh must not blank the report. */
    }
  }, [auditId]);

  const coverageNote = coverageNoteOf(audit);
  const storageNote = storageNoteOf(audit);
  const captureNote = captureNoteOf(audit);

  const [whiteLabelSettings, setWhiteLabelSettings] = useState(null);
  const [userPlan, setUserPlan] = useState('FREE');
  const [user, setUser] = useState(null);
  const [showPDFUpsell, setShowPDFUpsell] = useState(false);

  // Wall clock, ticked once a second while the audit runs. The ELAPSED value
  // itself is derived below from audit.created_at — this is only the thing
  // that makes the derivation re-render.
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Fallback origin for the one case where the record carries no created_at.
  // A missing timestamp should degrade to the old behaviour, not to NaN on
  // the screen.
  const mountedMs = useRef(Date.now());

  // Tier access logic — paid access is PER-AUDIT, always. Nothing unlocks a
  // report on the strength of a plan name.
  //
  // This read `audit?.is_paid || ['PRO','MAX'].includes(userPlan)` until
  // 2026-08-31. The subscription-wide half was dead — those tiers stopped being
  // purchasable on 2026-07-16 and production carries zero accounts on them —
  // but it was also the one path that could open a report the customer had not
  // paid for, on nothing but a string from /auth/me.
  //
  // The per-audit rule was already the stated intent for STARTER, and its
  // reason still holds: paying for a new audit must not retroactively open old
  // reports. A recurring plan that includes past reports would need a
  // deliberate decision and a server-side check, not a plan-name comparison in
  // a component.
  /* ── ENTITLEMENT IS THE SERVER'S ANSWER. THIS FILE DOES NOT RE-DERIVE IT ──
   *
   * `viewer_entitled` is set by get_audit on the SAME expression that decides
   * the free-tier trim, so the two cannot disagree. Until 2026-09-03 they did:
   * the server trimmed on `is_admin or is_paid` and this file paywalled on
   * `is_paid` alone, so an ADMIN viewing a FREE audit got an untrimmed report
   * (correct) with a $49 upgrade banner drawn over nine fully open findings
   * (not). Two predicates for one question - the same "second paywall with a
   * second set of bugs" this file already warns about for `locked`.
   *
   * `=== true` rather than a truthiness cast, and the default is UNENTITLED:
   * a payload without the field (an older cached response, a thin test stub)
   * must not be read as full access.
   *
   * `isPaid` survives for the one thing that is genuinely about the AUDIT
   * rather than the viewer - the plan badge. Everything paywall-shaped below
   * reads isEntitled. */
  const isEntitled = audit?.viewer_entitled === true;
  const isPaid = !!audit?.is_paid;
  const isPro = isEntitled;
  const isMax = false;

  useEffect(() => {
    let interval;
    const fetchAudit = async () => {
      try {
        const resp = await axios.get(`${API}/audits/${auditId}${viewQuery}`, { withCredentials: true });
        setAudit(resp.data);
        setError(null);
        if (!['PROCESSING', 'PENDING'].includes(resp.data.status)) {
          if (interval) clearInterval(interval);
          try {
            const ssResp = await axios.get(`${API}/audits/${auditId}/screenshots${viewQuery}`, { withCredentials: true });
            setScreenshots(toSrcList(ssResp.data.screenshot_urls, ssResp.data.screenshots));
            setScreenshotsMobile(toSrcList(ssResp.data.screenshot_mobile_urls, ssResp.data.screenshots_mobile));
          } catch {}
        }
      } catch (e) {
        console.error('Failed to load audit:', e);
        if (e.response?.status === 404) {
          setError('Audit not found');
        } else {
          setError('Failed to load audit. Please try again.');
        }
      }
      finally { setLoading(false); }
    };
    fetchAudit();
    interval = setInterval(fetchAudit, 5000);
    return () => clearInterval(interval);
  }, [auditId]);

  // Elapsed clock for the processing screen. Stops as soon as the audit leaves
  // PENDING/PROCESSING so a delivered report never carries a running timer.
  //
  // ── IT COUNTS FROM created_at, NOT FROM MOUNT ─────────────────────────────
  //
  // It used to be `setElapsedSec(s => s + 1)` off a useState(0), which measured
  // SECONDS THIS TAB HAD BEEN OPEN. The label says "elapsed" and the screen
  // says "you can leave this page", so a customer who refreshed — or came back
  // from the email — watched the clock restart at 00:00 on an audit that had
  // been running for four minutes. The number was real and it was measuring
  // the wrong thing, which is worse than an obviously fake one: it invites
  // trust it has not earned.
  //
  // created_at was already on the record and already parsed 160 lines below
  // for the audit_completed duration, so the fix reads a value the component
  // already had.
  //
  // Math.max(0, ...) because the client clock is not the server clock. A
  // browser a few seconds behind would otherwise render a negative age, and
  // -00:03 on a progress screen reads as a bug in the audit rather than in the
  // laptop.
  const isRunning = ['PROCESSING', 'PENDING'].includes(audit?.status);
  useEffect(() => {
    if (!isRunning) return undefined;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isRunning]);
  const startedMs = audit?.created_at ? Date.parse(audit.created_at) : NaN;
  const originMs = Number.isFinite(startedMs) ? startedMs : mountedMs.current;
  const elapsedSec = Math.max(0, Math.floor((nowMs - originMs) / 1000));
  const elapsedLabel = `${String(Math.floor(elapsedSec / 60)).padStart(2, '0')}:${String(elapsedSec % 60).padStart(2, '0')}`;

  // Fetch user plan
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUserPlan(resp.data.plan || 'FREE');
        setUser(resp.data);
      } catch (e) {
        // Default to FREE
      }
    };
    fetchUserPlan();
  }, []);

  // Fetch white-label settings
  useEffect(() => {
    const fetchWhiteLabel = async () => {
      try {
        const resp = await axios.get(`${API}/settings/white-label`, { withCredentials: true });
        if (resp.data && resp.data.enabled) {
          setWhiteLabelSettings(resp.data);
        }
      } catch (e) {
        // Silently fail - will show default branding
      }
    };
    fetchWhiteLabel();
  }, []);

  const handleDownloadPDF = async () => {
    if (!isEntitled) {
      track('upgrade_clicked', { source: 'report_blur', cta: 'pdf_download_locked', audit_id: auditId });
      setShowPDFUpsell(true);
      return;
    }
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/audits/${auditId}/pdf${viewQuery}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${whiteLabelSettings?.agency_name || 'UXAuditPro'}-${audit?.app_name || 'Report'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const [viewport, setViewport] = useState(VIEWPORT_ALL);

  const toggleFinding = (index) => {
    setExpandedFindings(prev => ({ ...prev, [index]: !prev[index] }));
  };

  /* Cross-screen consistency. Non-empty only on a multi-screen capture, and
     only after the validator matched every named screen against the
     capture's own labels. An empty array is a real answer - "the set is
     consistent" - so the section renders nothing rather than an empty state
     that would read as a missing feature. */
  const consistency = useMemo(
    () => audit?.consistency_findings || audit?.v2_report?.consistency_findings || [],
    [audit],
  );

  // Normalise findings from either v1 (audit.findings flat array) or
  // v2 (audit.v2_report.dimensions.*.findings) pipeline output.
  const findings = useMemo(() => {
    if (audit?.findings?.length > 0) return audit.findings;

    const dims = audit?.v2_report?.dimensions;
    if (!dims) return [];

    // v2 pipeline emits capitalised severities (Critical|Major|Minor) and
    // descriptive effort strings (Quick Fix (1-2h)|Moderate (1-2d)|Major Effort
    // (1w+)). Normalise both to the lowercase v1 convention the UI compares
    // against, otherwise stats, locked-finding counts and lock gating all
    // silently resolve to 0.
    const normaliseSeverity = (sev) => {
      const s = String(sev || 'minor').toLowerCase();
      if (s === 'moderate') return 'major';
      if (['critical', 'major', 'minor'].includes(s)) return s;
      return 'minor';
    };
    const normaliseEffort = (effort) => {
      const e = String(effort || '').toLowerCase();
      if (e.includes('quick') || e === 'quick-win' || e === 'low') return 'low';
      if (e.includes('major') || e === 'strategic' || e === 'high') return 'high';
      return 'medium';
    };

    return Object.entries(dims).flatMap(([dimKey, dimData]) =>
      (dimData?.findings || []).map(f => ({
        title: f.issue || f.title || '',
        description: f.evidence || f.description || '',
        severity: normaliseSeverity(f.severity),
        dimension: dimKey,
        effort: normaliseEffort(f.effort),
        impact: String(f.impact || 'medium').toLowerCase(),
        recommendation: f.recommendation || '',
        annotation: f.annotation || null,
        region: f.region || null,
      }))
    );
  }, [audit]);

  const quickWins = useMemo(() => {
    if (!findings.length) return [];
    const low = findings.filter(f => f.effort === 'low' && ['high', 'medium'].includes(f.impact));
    if (low.length >= 3) return low.slice(0, 3);
    return findings.filter(f => f.severity === 'critical').slice(0, 3);
  }, [findings]);

  const stats = useMemo(() => {
    if (!findings.length) return { critical: 0, major: 0, minor: 0, quickFixes: 0 };
    return {
      critical: findings.filter(f => f.severity === 'critical').length,
      major: findings.filter(f => f.severity === 'major').length,
      minor: findings.filter(f => f.severity === 'minor').length,
      quickFixes: findings.filter(f => f.effort === 'low').length,
    };
  }, [findings]);

  /* ── VIEWPORT FILTER + COMPONENT SPINE (redesign Phase 3a) ────────────
   *
   * THESE THREE MEMOS MUST STAY ABOVE THE EARLY RETURNS BELOW, WITH THE
   * OTHER HOOKS. They were originally written further down, beside the
   * render code that consumes them, which put them AFTER
   *
   *     if (loading) return ...
   *     if (error || !audit) return ...
   *     if (status is PROCESSING/PENDING) return ...
   *     if (status is FAILED) return ...
   *
   * so the first render (loading) ran N hooks and the render after the
   * audit arrived ran N+3. That is React error #310, "rendered more hooks
   * than during the previous render", and it does not degrade - it unmounts
   * the tree, so EVERY report page painted its background and nothing else.
   * Web audits and uploads alike; the finding data made no difference.
   *
   * Read the region below before adding a hook to it: everything after the
   * early returns is deliberately hook-free, and `captureFindings` down
   * there is a plain const for exactly this reason.
   *
   * `is_mobile` is the SERVER's field, written by _findings_for_ui from
   * report_format.is_mobile_finding. It is not re-derived here. There is a
   * separate local isMobileFinding further down, and it is a DIFFERENT
   * QUESTION: it decides which capture to draw a pin on and legitimately
   * reads annotation.viewport, which is about the image, not the finding.
   * Do not merge the two - the filter must agree with the server, and the
   * pin must agree with the screenshot.
   *
   * Filtering happens BEFORE grouping so a group's count is the count of
   * what is actually under it. Grouping first and hiding rows afterwards
   * would print "Footer 4" over one visible row.
   */
  const viewportChipCounts = useMemo(() => viewportCounts(findings), [findings]);
  /* How many findings the SERVER actually locked on this payload. The banner
     needs it, and it is the observable half of the entitlement pair above:
     `!isEntitled` says the server decided to withhold, this says it did. */
  const lockedCount = useMemo(
    () => findings.length - openFindingCount(findings), [findings],
  );
  const filteredFindings = useMemo(
    () => findings.map((f, index) => ({ finding: f, index }))
      .filter(({ finding }) => matchesViewport(finding, viewport)),
    [findings, viewport],
  );
  const {
    groups: visibleGroups,
    ungrouped: visibleUngrouped,
    absorbedLine,
  } = useMemo(() => {
    // groupByComponent numbers from its own input, so it is given the
    // filtered list and the ORIGINAL index is carried back through.
    const g = groupByComponent(filteredFindings.map(({ finding }) => finding));
    const reindex = (m) => ({ ...m, index: filteredFindings[m.index].index });
    return {
      groups: g.groups.map((grp) => ({ ...grp, members: grp.members.map(reindex) })),
      ungrouped: g.ungrouped.map(reindex),
      // The sections that were dropped for having no card of their own, as
      // one sentence. Empty on most reports.
      absorbedLine: absorbedSummary(g.absorbed),
    };
  }, [filteredFindings]);

  // The denominator for the StatTile meters: the three severities sum to the
  // finding count by construction (every finding has exactly one), so the bars
  // are proportions of a real total rather than of an assumed maximum.
  const findingTotal = stats.critical + stats.major + stats.minor;

  // ── Telemetry ──────────────────────────────────────────────────────────
  // report_viewed / audit_completed fire once per page view; scroll depth
  // and time-on-page accumulate in refs and flush as report_view_summary on
  // pagehide/unmount; paywall_hit fires when the first blurred card enters
  // the viewport. All calls are no-ops if PostHog didn't load.
  const viewTracked = useRef(false);
  const prevStatusRef = useRef(null);
  const paywallTracked = useRef(false);
  const summarySent = useRef(false);
  const maxScrollPct = useRef(0);
  const viewStartMs = useRef(null);

  useEffect(() => {
    if (!audit) return;
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = audit.status;
    if (audit.status !== 'DELIVERED') return;

    // audit_completed: only when we witnessed the processing → delivered
    // transition in this session (page loads on an already-delivered audit
    // must not re-count completions).
    if (['PROCESSING', 'PENDING'].includes(prevStatus)) {
      const created = audit.created_at ? Date.parse(audit.created_at) : null;
      const completed = audit.completed_at ? Date.parse(audit.completed_at) : null;
      track('audit_completed', {
        audit_id: auditId,
        duration_ms: created && completed ? Math.max(0, completed - created) : null,
        score: audit.overall_score ?? null,
        finding_count: findings.length,
        critical_count: findings.filter(f => f.severity === 'critical').length,
      });
    }

    if (!viewTracked.current) {
      viewTracked.current = true;
      viewStartMs.current = Date.now();
      const completed = audit.completed_at ? Date.parse(audit.completed_at) : null;
      track('report_viewed', {
        audit_id: auditId,
        tier: userPlan,
        is_paid: !!audit.is_paid,
        time_since_completion_ms: completed ? Math.max(0, Date.now() - completed) : null,
      });
    }
  }, [audit, auditId, userPlan, findings]);

  // Scroll depth + time on page, flushed once as report_view_summary.
  useEffect(() => {
    if (!audit || audit.status !== 'DELIVERED') return;
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const pct = scrollable > 0
        ? Math.min(100, Math.round((window.scrollY / scrollable) * 100))
        : 100;
      if (pct > maxScrollPct.current) maxScrollPct.current = pct;
    };
    const sendSummary = () => {
      if (!viewTracked.current || summarySent.current) return;
      summarySent.current = true;
      track('report_view_summary', {
        audit_id: auditId,
        tier: userPlan,
        scroll_depth_pct: maxScrollPct.current,
        time_on_page_ms: viewStartMs.current ? Date.now() - viewStartMs.current : null,
        paywall_seen: paywallTracked.current,
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', sendSummary);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', sendSummary);
      sendSummary();
    };
    // Deliberate dep list: the DELIVERED transition and the audit id, not
    // every object this effect reads. It carried an eslint-disable naming
    // react-hooks/exhaustive-deps, which this build's config never enables —
    // a directive for an undefined rule, and an eslint ERROR the first time
    // the build ran without a warm cache. See docs/DECISIONS.md.
  }, [audit?.status === 'DELIVERED', auditId]);

  // paywall_hit: first blurred finding card entering the viewport.
  useEffect(() => {
    if (!audit || audit.status !== 'DELIVERED' || isEntitled) return;
    if (paywallTracked.current || typeof IntersectionObserver === 'undefined') return;
    const el = document.querySelector('[data-paywall-marker]');
    if (!el) return;
    // COUNTED, not assumed. This was `findings.length - 1`, which shadowed
    // the real count and reported a blurred_finding_count of 8 on a payload
    // where nothing was blurred at all - the same arithmetic that made the
    // banner say "1 of 9" over nine open findings.
    const observer = new IntersectionObserver((entries) => {
      if (paywallTracked.current) return;
      if (entries.some(e => e.isIntersecting)) {
        paywallTracked.current = true;
        track('paywall_hit', {
          audit_id: auditId,
          blurred_finding_count: lockedCount,
          scroll_depth_pct: maxScrollPct.current,
        });
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
    // Deliberate dep list, same call as the effect above.
  }, [audit?.status === 'DELIVERED', isEntitled, findings, auditId]);

  // Loading State
  if (loading) return (
    <div className="min-h-screen pt-20 flex items-center justify-center">
      <span className="uxa-run-spinner" role="status" aria-label="Loading report" />
    </div>
  );

  // Error State
  if (error || !audit) return (
    /* data-testid: this branch had NO named element, which meant the render
       harness could not assert it rendered and had to fall back to counting
       characters on a document that is mostly nav and footer. Every other
       branch of this component declares itself - processing-screen,
       failed-state - and this one now does too. A page that cannot say what
       it is supposed to show cannot be checked for showing it. */
    <div className="min-h-screen pt-20 flex items-center justify-center px-6" data-testid="report-error">
      <div className="uxa-app-card" style={{ maxWidth: '480px', width: '100%' }}>
        <div className="uxa-form-card-spectrum" aria-hidden="true" />
        <div className="text-center" style={{ padding: '40px 32px' }}>
          <span className="uxa-fail-mark uxa-fail-mark--bad" style={{ width: '40px', height: '40px', margin: '0 auto', fontSize: '18px' }} aria-hidden="true">
            <AlertTriangle className="w-5 h-5" />
          </span>
          <p style={{ fontSize: 'var(--type-h3-size)', fontWeight: 'var(--type-h3-weight)', color: 'var(--ink-1)', margin: '18px 0 0' }}>{error || 'Audit not found'}</p>
          <Link to="/dashboard" className="uxa-button-secondary" style={{ marginTop: '24px' }}>Back to Dashboard</Link>
        </div>
      </div>
    </div>
  );

/* ═══ LIVE PIPELINE STAGES ══════════════════════════════════════════════════
 *
 * The processing screen used to carry this, and it was true when written:
 *
 *   "WE DO NOT HAVE THAT DATA. The pipeline reports a status ... not a
 *    percentage, and there is no per-stage progress field on the audit
 *    document. A synthetic percentage would be a fabricated number on a
 *    screen whose entire job is telling the customer the truth about where
 *    they are. ... A true percentage needs a backend progress field; that is
 *    a pipeline change, not a styling one."
 *
 * The pipeline change is done. `audit.pipeline_stage` is now written by the
 * collector at each boundary it actually crosses, so this list marks real
 * progress instead of pinning stage 1 on and hoping.
 *
 * FOUR STAGES, NOT THREE, AND IN THIS ORDER, because that is the order the
 * code runs in: the desktop page is captured, the DOM is measured in that same
 * desktop context, and only then is a second browser context opened for
 * mobile. Measuring sits BETWEEN the two captures. The old three-item list
 * implied capture-then-measure, which no one would guess was wrong and which
 * would have made the marks jump backwards the moment they were driven by
 * real data.
 *
 * STILL NO PERCENTAGE. Four known stages is not a completion ratio - the
 * analysis stage alone can outlast the other three - so the numeral slot keeps
 * the elapsed clock, which is measured, and the track stays indeterminate.
 * Knowing which stage we are in is not the same as knowing how much is left,
 * and the screen should not imply otherwise.
 */
const PIPELINE_STAGES = [
  { key: 'desktop', name: 'Capture desktop', note: 'Full page at 1440 wide' },
  { key: 'measure', name: 'Measure', note: 'Contrast ratios, tap targets, layout checks' },
  { key: 'mobile', name: 'Capture mobile', note: 'Full page at 390 wide' },
  { key: 'analyze', name: 'Analyze & verify', note: 'Findings without evidence are discarded' },
];

/* Which list index each backend stage corresponds to. `queued` is -1: nothing
 * has started, so nothing is ticked. An unrecognised value also yields -1
 * rather than a guess — a new backend stage should show an honest "starting"
 * rather than silently light up whichever mark happens to be nearest. */
const STAGE_INDEX = {
  queued: -1,
  capturing_desktop: 0,
  measuring: 1,
  capturing_mobile: 2,
  analyzing: 3,
  prose: 3,
  complete: 4,
};

const stageIndex = (stage) => {
  const i = STAGE_INDEX[stage];
  return i === undefined ? -1 : i;
};

const stageState = (i, stage) => {
  const current = stageIndex(stage);
  if (i < current) return 'done';
  if (i === current) return 'active';
  return 'pending';
};

const stageLabel = (stage) => {
  // A MULTI-PAGE RUN WRITES ITS OWN STAGE STRINGS. `capturing page 2 of 3`
  // matched no case here and fell through to "Starting your audit...", so a
  // three-page customer watched that sentence for up to seven minutes on a page
  // whose own copy promises five. The work was happening and nothing said so.
  const pages = /^capturing page (\d+) of (\d+)$/.exec(stage || '');
  if (pages) return `Capturing page ${pages[1]} of ${pages[2]}...`;

  switch (stage) {
    case 'capturing_desktop': return 'Capturing your page at 1440 wide...';
    case 'measuring': return 'Measuring contrast and tap targets...';
    case 'capturing_mobile': return 'Capturing the mobile view at 390 wide...';
    case 'analyzing': return 'Analyzing your app...';
    case 'prose': return 'Writing up the findings...';
    default: return 'Starting your audit...';
  }
};

/* Past tense only for work that is genuinely behind us. */
const stagePulse = (stage) => {
  const current = stageIndex(stage);
  if (current >= 3) return 'Desktop and mobile captured at 1440 and 390 wide';
  if (current >= 2) return 'Desktop captured at 1440 wide \u2014 mobile next';
  if (current >= 1) return 'Desktop captured at 1440 wide';
  return 'Capturing at 1440 wide, then 390';
};

  // Processing State — the "Audit running" artboard of the App States design.
  //
  // The design's centrepiece is a percentage: "41%" in Anton, with a filled
  // progress bar and a four-line capture log. WE DO NOT HAVE THAT DATA. The
  // pipeline reports a status (PENDING / PROCESSING / DELIVERED / FAILED), not
  // a percentage, and there is no per-stage progress field on the audit
  // document. A synthetic percentage would be a fabricated number on a screen
  // whose entire job is telling the customer the truth about where they are.
  //
  // So the numeral slot holds the ELAPSED CLOCK, which is real and is a
  // numeral (the only thing Anton is allowed to set), the track is an
  // indeterminate sweep rather than a fake fill, and the stage list is driven
  // by the status we actually have. Everything else — the card, the header
  // row, the two-column split, the stage marks, the pulse row — is the design
  // as drawn. A true percentage needs a backend progress field; that is a
  // pipeline change, not a styling one.
  if (['PROCESSING', 'PENDING'].includes(audit.status)) return (
    <div className="min-h-screen pt-20">
      <div className="mx-auto px-6 py-12" style={{ maxWidth: '1180px' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="uxa-app-card"
          data-testid="processing-screen"
        >
          <div className="uxa-form-card-spectrum" aria-hidden="true" />

          <div className="uxa-app-card-head">
            <div className="flex items-center gap-3 min-w-0">
              <span className="uxa-app-logo" aria-hidden="true">UX</span>
              <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ink-1)' }} className="truncate">
                {audit.app_name || audit.input_data || 'UX Audit'}
              </span>
              <span className="uxa-chip">#{String(auditId).slice(0, 8)}</span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="uxa-button-secondary"
              data-testid="processing-leave"
            >
              Back to dashboard
            </button>
          </div>

          <div className="uxa-run">
            <div className="uxa-run-main">
              <div className="uxa-run-stage">
                <span className="uxa-run-spinner" aria-hidden="true" />
                <span data-testid="processing-stage-label">{stageLabel(audit.pipeline_stage)}</span>
              </div>

              <div className="flex items-end gap-3" style={{ marginTop: '24px' }}>
                <span className="uxa-run-pct" data-testid="processing-elapsed">{elapsedLabel}</span>
                <span style={{ fontSize: '16px', fontWeight: 500, color: 'var(--ink-2)', paddingBottom: '8px' }}>elapsed</span>
              </div>

              <div
                className="uxa-run-track uxa-run-track--indeterminate"
                role="progressbar"
                aria-label="Analysis in progress"
                aria-valuetext={`Running for ${elapsedLabel}`}
              >
                <div />
              </div>

              <p style={{ fontSize: '16px', lineHeight: 1.6, color: 'var(--ink-2)', margin: '20px 0 0', maxWidth: '420px' }}>
                Capturing your page, measuring it, and verifying every finding against the evidence.
                You can leave this page — we will email the report the moment it is ready.
              </p>
            </div>

            <div className="uxa-run-side">
              <div className="uxa-eyebrow">Stages</div>
              <ol className="flex flex-col" style={{ gap: '2px', marginTop: '18px', listStyle: 'none', margin: '18px 0 0', padding: 0 }}>
                {PIPELINE_STAGES.map((st, i) => {
                  const state = stageState(i, audit.pipeline_stage);
                  return (
                    <li
                      key={st.name}
                      className={`uxa-stage${state === 'active' ? ' uxa-stage--active' : ''}${state === 'done' ? ' uxa-stage--done' : ''}`}
                      data-testid={`stage-${st.key}`}
                      data-state={state}
                    >
                      <span className="uxa-stage-mark" aria-hidden="true">
                        {state === 'done' ? '\u2713' : i + 1}
                      </span>
                      <span className="flex-1">
                        <span className="uxa-stage-name">{st.name}</span>
                        <span className="uxa-stage-note">{st.note}</span>
                      </span>
                    </li>
                  );
                })}
              </ol>
              {/* WAS: "Desktop and mobile captured at 1440 and 390 wide",
                  shown from the first second of the run - a completed-tense
                  claim about work that had not started. It now reports the
                  viewport actually in hand, and says so in the past tense only
                  once it is. */}
              <div className="uxa-pulse-row">
                <span className="uxa-pulse" aria-hidden="true" />
                {stagePulse(audit.pipeline_stage)}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );

  // Failed State
  //
  // The copy and the retry button are driven by `failure_category`, set by the
  // backend (see backend/audit/failure_classes.py). This screen used to say
  // "Something went wrong. Please try again." for every cause and always offer
  // a retry. On 2026-08-17 audits failed because OUR model-provider credit had
  // run out; the retry could not have succeeded, and we invited customers to
  // keep pressing it.
  //
  // `error_detail` is only ever populated for page-collection failures, where
  // the backend writes a message intended for a customer to read. No other
  // category's internal error string reaches the browser.
  if (audit.status === 'FAILED') {
    const category = audit.failure_category || 'unknown';
    const detail = category === 'page_unreachable' ? (audit.error_message || '') : '';

    let heading = 'Analysis Failed';
    let body = 'Something went wrong. Please try again.';
    let canRetry = true;
    // A route that CAN succeed, when retrying the same thing cannot.
    let altAction = null;

    if (category === 'provider_unavailable') {
      heading = 'We could not complete this audit';
      // Ours to fix, and saying so is the honest version. No retry: the cause
      // is on our side and pressing it again would fail identically.
      //
      // The money sentence differs by plan and both halves are true of the
      // backend as written. A FAILED audit is excluded from the free-tier
      // lifetime count (server.py check_audit_quota), so a free user's audit is
      // genuinely unspent. A STARTER user HAS already paid at checkout and is
      // not refunded — what they get is a re-run at no extra charge, via the
      // completed-payment-linked-to-a-FAILED-audit branch of the same function.
      // Do not collapse these into one line; "you have not been charged" is
      // false for a paying customer.
      body = audit.is_paid
        ? 'Our team has been notified and is looking into it. Your payment is safe — you can re-run this audit at no extra charge once it is fixed.'
        : 'Our team has been notified and is looking into it. This has not used your free audit.';
      canRetry = false;
    } else if (category === 'analysis_incomplete') {
      // The pipeline ran and produced a result we refuse to sell — zero verified
      // findings alongside a score asserting severe problems. That contradiction
      // is a failure on our side, not a verdict on the customer's site, and the
      // copy must not imply we judged their product.
      //
      // NO RETRY. Observed live 2026-08-17: a thameswater.co.uk audit failed
      // here, the automatic retry failed with the identical error, and the UI
      // then offered a third attempt. Re-running the same page reproduces it.
      heading = 'We could not complete this audit';
      // A dead end is not good enough. For a URL audit this almost always means
      // we could not get a real view of the page — a consent wall, a bot check,
      // or a JS shell that never resolved — and the UPLOAD path bypasses
      // collection entirely, so it works on exactly the sites the crawler
      // cannot reach. Point the customer at the route that succeeds instead of
      // only apologising. `_blast_upload_suggestion_to_failed_users` sends the
      // same advice by email; this is the on-page version.
      const isUrlAudit = (audit.input_type || 'URL') === 'URL';
      const money = audit.is_paid
        ? 'Your payment is safe — this can be re-run at no extra charge.'
        : 'This has not used your free audit.';
      body = isUrlAudit
        ? `The analysis did not produce a result we are willing to send you. That is a problem on our side, not a verdict on your site — most often it means we could not get a full view of the page, because of a cookie wall, a bot check, or scripts that never finished loading. Uploading a screenshot skips that step entirely and usually works. ${money}`
        : `The analysis did not produce a result we are willing to send you. That is a problem on our side, not a verdict on your design. Our team has been notified. ${money}`;
      canRetry = false;
      // Not a retry of the same thing — a different route that can succeed.
      altAction = isUrlAudit
        ? { to: '/audit/new', label: 'Upload a screenshot instead' }
        : null;
    } else if (category === 'page_unreachable' && detail) {
      heading = 'We could not read that page';
      body = detail;
    }

    // "Audit could not complete" — the 02 / FAILED artboard. States the cause in
    // plain language, shows how far it got, and offers a way forward. The copy,
    // the retry gate and the alternate action are unchanged; only the shell is
    // new. The "What happened" panel reports the three real checkpoints the
    // backend distinguishes, never a stack trace.
    const reachedCapture = category !== 'page_unreachable';
    return (
      <div className="min-h-screen pt-20">
        <div className="mx-auto px-6 py-12" style={{ maxWidth: '1180px' }}>
          <div
            className="uxa-app-card"
            data-testid="failed-state"
            data-failure-category={category}
          >
            <div className="uxa-form-card-spectrum" aria-hidden="true" />
            <div style={{ padding: '44px 40px' }}>
              <div className="uxa-fail">
                <div>
                  {/* PHASE G: the THIRD hand-rolled copy of the severity chip,
                      meter and all. SeverityBadge takes a label override for
                      this status use, so the failure banner and the finding
                      chips are one component again. */}
                  <SeverityBadge severity="major" label="Audit stopped" />
                  <h2 style={{ fontSize: 'var(--type-h2-size)', fontWeight: 'var(--type-h2-weight)', lineHeight: 'var(--type-h2-leading)', letterSpacing: 'var(--type-h2-tracking)', color: 'var(--ink-1)', margin: '20px 0 0', maxWidth: '520px' }}>
                    {heading}
                  </h2>
                  <p style={{ fontSize: 'var(--type-lead-size)', lineHeight: 'var(--type-lead-leading)', color: 'var(--ink-2)', margin: '14px 0 0', maxWidth: '520px' }}>
                    {body}
                  </p>

                  <div className="flex flex-wrap gap-3" style={{ marginTop: '32px' }}>
                    {canRetry ? (
                      <Link to="/audit/new" className="uxa-submit uxa-submit--auto" data-testid="failed-retry">
                        Try Again
                        <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                      </Link>
                    ) : (
                      <>
                        {altAction && (
                          <Link to={altAction.to} className="uxa-submit uxa-submit--auto" data-testid="failed-alt-action">
                            {altAction.label}
                            <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                          </Link>
                        )}
                        <Link to="/dashboard" className="uxa-button-secondary" data-testid="failed-back">
                          Back to dashboard
                        </Link>
                      </>
                    )}
                  </div>

                  <p style={{ fontSize: '16px', fontWeight: 500, color: 'var(--ink-2)', margin: '20px 0 0' }}>
                    Still stuck?{' '}
                    <Link to="/contact/" style={{ color: 'var(--link)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                      Contact support
                    </Link>
                    .
                  </p>
                </div>

                <div className="uxa-fail-panel">
                  <div className="uxa-eyebrow">What happened</div>
                  <div className="flex flex-col" style={{ gap: '14px', marginTop: '18px' }}>
                    <div className="uxa-fail-step">
                      <span className={`uxa-fail-mark uxa-fail-mark--${reachedCapture ? 'ok' : 'bad'}`} aria-hidden="true">
                        {reachedCapture ? '\u2713' : '\u2715'}
                      </span>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink-1)' }}>Page collected</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--ink-2)' }}>
                          {reachedCapture ? 'We got a view of the page' : 'We could not read the page'}
                        </div>
                      </div>
                    </div>
                    <div className="uxa-fail-step">
                      <span className={`uxa-fail-mark uxa-fail-mark--${reachedCapture ? 'bad' : 'skip'}`} aria-hidden="true">
                        {reachedCapture ? '\u2715' : '\u2013'}
                      </span>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: reachedCapture ? 'var(--ink-1)' : 'var(--ink-3)' }}>Analysis</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: reachedCapture ? 'var(--ink-2)' : 'var(--ink-3)' }}>
                          {reachedCapture ? 'Did not produce a result we would send you' : 'Not started'}
                        </div>
                      </div>
                    </div>
                    <div className="uxa-fail-step">
                      <span className="uxa-fail-mark uxa-fail-mark--skip" aria-hidden="true">{'\u2013'}</span>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink-3)' }}>Report</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--ink-3)' }}>Not delivered</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ height: '1px', background: 'var(--border-2)', margin: '24px 0' }} />
                  <div className="uxa-meta" style={{ lineHeight: 1.9 }}>
                    <div>ref &middot; {String(auditId).slice(0, 8)}</div>
                    {audit.created_at && <div>{new Date(audit.created_at).toLocaleString()}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const summaries = audit.dimension_summaries || {};
  const overallScore = audit.overall_score || 0;
  /* Mirrors backend multi_page.score_label. `pages_requested` rather than the
     captured list, so an audit that asked for three and captured one still
     labels its score — the reader was expecting three. */
  const pagesRequested = audit.pages_requested || [];
  /* Already validated server-side — every one names at least two audited pages
     and a concrete difference. The component renders what survived that rule
     and does not re-judge it; a second, weaker rule here would be a second
     place for the two to disagree. */
  const crossPageFindings = audit.cross_page_findings || [];
  const extraPages = audit.extra_pages || [];
  const isMultiPage = pagesRequested.length > 1;
  const scoreLabel = isMultiPage
    ? `Page 1 score — ${pagesRequested[0]}`
    : 'Overall UX Score';

  /* Findings for the capture pair, numbered once so a marker and its row
   * always carry the same number.
   *
   * `pos` is null unless the finding carries REAL annotation coordinates.
   * getAnnotationPosition falls back to a region guess plus an index-derived
   * nudge when they are missing, which would place a pin somewhere nothing was
   * measured — so the fallback is deliberately not used here. A finding with no
   * coordinates still gets a row; it just does not get a pin, and the row says
   * so. Capped at 6 markers as before: beyond that the capture becomes a
   * pincushion and the numbers stop being findable.
   */
  /* ═══ ONE FINDING, ONE CAPTURE ══════════════════════════════════════════
   *
   * Six markers used to be stamped on a single desktop screenshot. Two things
   * were wrong with that and only the first is a layout preference.
   *
   * 1. A pincushion. Six numbered dots over one hero, and the reader has to
   *    hold the mapping between a dot and a paragraph in their head.
   *
   * 2. THE MOBILE MARKERS WERE ON THE WRONG IMAGE. `annotation` is
   *    {x, y, ui_element} with NO VIEWPORT, and the API only ever sent the
   *    desktop captures - screenshots_mobile was collected, stored, and never
   *    projected. So a finding sourced from mobile_screenshot ("15 mobile tap
   *    targets are below 44x44", "all eight mobile nav links are 0px apart")
   *    had its mobile x/y drawn on the 1440px desktop image, landing
   *    somewhere arbitrary. A percentage means nothing until you say which
   *    image it is a percentage OF.
   *
   * Each finding now gets its own row: the capture its evidence actually came
   * from, and its explanation beside it.
   *
   * WHICH CAPTURE, and how confident we are about the pin:
   *
   *   viewport stated           use it, and pin. New audits carry
   *                             annotation.viewport (added to the analyzer
   *                             contract in this change).
   *   absent, desktop-sourced   desktop capture, and pin. The backend's own
   *                             comment records the historical assumption:
   *                             "the `screenshots` list is what the report UI
   *                             annotates with desktop x/y coordinates".
   *   absent, mobile-sourced    mobile capture, and NO PIN. The coordinates
   *                             cannot be attributed to either image, and a
   *                             marker in the wrong place is worse than none.
   *                             The row says so rather than leaving a gap.
   */
  const isMobileFinding = (f) => (
    f.annotation?.viewport === 'mobile'
    || f.dimension === 'mobileUX'
    || f.evidence?.source === 'mobile_screenshot'
  );

  /* One renderer for both states, so a locked row and an open row cannot
   * drift apart. `locked` is the SERVER's answer: free_view._teaser_finding
   * sets it after stripping evidence, description and fix and after passing
   * the title through the teaser rule that bans a digit. Recomputing it from
   * isPaid here would be a second paywall with a second set of bugs - the
   * same mistake the consistency section already fixed. */
  const renderFinding = (finding, i) => {
    if (isLocked(finding)) {
      return (
        <LockedContent
          key={i}
          title={finding.title}
          severity={finding.severity}
          dimension={finding.dimension}
          onUnlock={() => {
            track('upgrade_clicked', { source: 'report_blur', cta: 'locked_finding', audit_id: auditId });
            setShowPDFUpsell(true);
          }}
        />
      );
    }
    return (
      <FindingCard
        key={i}
        finding={finding}
        index={i}
        /* The free tier's one open finding opens expanded: the evidence is
           the argument, and hiding it behind a click wastes it. The server
           chose which one (order_findings_for_free_tier promotes the
           highest-severity desktop finding carrying a measured value) and
           teasered the rest, so this is the only unlocked entry. */
        isExpanded={(!isEntitled && i === 0) || expandedFindings[i]}
        onToggle={() => toggleFinding(i)}
      />
    );
  };

  const captureFindings = findings.slice(0, 6).map((finding, i) => {
    const wantsMobile = isMobileFinding(finding);
    const hasMobileCapture = screenshotsMobile.length > 0;
    const useMobile = wantsMobile && hasMobileCapture;

    const hasCoords = finding.annotation
      && typeof finding.annotation.x === 'number'
      && typeof finding.annotation.y === 'number';
    const statedViewport = finding.annotation?.viewport;
    // Pin only when the coordinates provably belong to the image being shown.
    const attributable = hasCoords && (
      statedViewport
        ? (statedViewport === 'mobile') === useMobile
        : !wantsMobile              // legacy: desktop coordinates only
    );

    return {
      finding,
      num: i + 1,
      useMobile,
      wantsMobile,
      // Mobile above-fold reads best for a tap-target finding; desktop
      // above-fold for everything else. index 0 is above_fold in both lists.
      src: useMobile ? screenshotsMobile[0] : screenshots[0],
      pos: attributable ? getAnnotationPosition(finding, i) : null,
    };
  }).filter((f) => f.src);

  return (
    <div className="min-h-screen pt-20 pb-32">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

        {/* ADMIN PREVIEW BANNER. It says which VIEW this is, and it is not
            decoration: a report trimmed to one open finding looks exactly like
            a report that failed to load its findings, and the whole point of
            this mode is to inspect a customer complaint. Without the banner an
            admin would be reading the trim as the bug.

            `admin_preview_as_customer` comes from the SERVER, on the same
            response that carried the trim, so the banner cannot claim a mode
            the payload is not actually in. Reading `?as=customer` from the URL
            here instead would draw it whenever the parameter is present, for a
            non-admin the server ignored. */}
        {audit?.admin_preview_as_customer && (
          <div
            data-testid="admin-preview-banner"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 16, flexWrap: 'wrap', marginBottom: 24, padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--surface-panel)',
              border: '1px solid var(--border-2)',
              color: 'var(--ink-2)', fontSize: 'var(--type-dense-size)',
            }}
          >
            <span>
              <strong style={{ color: 'var(--ink-1)' }}>Viewing as the customer.</strong>{' '}
              This is the trimmed report their plan receives, not the admin view.
            </span>
            <Link to={`/audit/${auditId}/report`} style={{ color: 'var(--accent-text)', fontWeight: 600 }}>
              Switch to the full admin view
            </Link>
          </div>
        )}

        {/* ===== REPORT HEADER ===== */}
        <FadeInSection data-section="report_header">
          {/* The free re-audit that comes with a paid audit (backend
              audit/reaudit.py): one, same page, within 30 days. The server
              sends free_reaudit_available_until only while it is open. A plain
              render below the early returns: no hooks here (CLAUDE.md). */}
          {audit.free_reaudit_available_until && audit.input_data && (
            <div className="mb-6 rounded-lg px-4 py-3 text-sm"
                 style={{ background: 'var(--surface-panel)', border: '1px solid var(--border-1)', color: 'var(--ink-1)' }}
                 role="note" data-testid="free-reaudit-banner">
              Your free re-audit of this page is available until{' '}
              {new Date(audit.free_reaudit_available_until).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
              Fix what you can, then run it to see what was resolved.{' '}
              <Link to={`/audit/new?url=${encodeURIComponent(audit.input_data)}`}
                    style={{ color: 'var(--accent-text)' }} className="font-semibold underline"
                    data-testid="free-reaudit-start">
                Start your free re-audit
              </Link>
            </div>
          )}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="p-2 -ml-2 rounded-full transition-colors" 
                style={{ color: 'var(--ink-2)' }}
                data-testid="report-back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <h1 className="font-heading text-xl sm:text-2xl" style={{ color: 'var(--ink-1)' }} data-testid="report-title">{audit.app_name || 'UX Audit Report'}</h1>
                  <span 
                    className="px-2.5 py-0.5 text-[10px] font-bold rounded-full uppercase"
                    style={{ 
                      background: isPaid ? 'rgba(168,85,247,0.2)' : 'rgba(100,116,139,0.2)',
                      color: isPaid ? '#A855F7' : 'var(--ink-2)'
                    }}
                  >
                    {userPlan === 'FREE' ? 'Free' : userPlan}
                  </span>
                </div>
                <p className="text-xs" style={{ color: 'var(--ink-2)' }}>
                  {audit.input_data && <span style={{ color: 'var(--ink-2)' }} className="break-all">{audit.input_data}</span>}
                  {audit.input_data && ' · '}
                  {audit.industry || 'General'} · {audit.created_at ? new Date(audit.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={handleCopyLink}
                data-testid="copy-link-btn"
                className="uxa-submit uxa-submit--auto uxa-submit--compact uxa-submit--secondary"
              >
                {copiedLink ? <Check className="w-4 h-4" style={{ color: 'var(--success)' }} /> : <Copy className="w-4 h-4" />}
                <span className="hidden sm:inline">{copiedLink ? 'Copied!' : 'Copy Link'}</span>
              </button>
              {isEntitled ? (
                <button
                  onClick={handleDownloadPDF}
                  disabled={downloading}
                  data-testid="download-pdf-btn"
                  className="uxa-submit uxa-submit--auto uxa-submit--compact uxa-submit--secondary"
                >
                  {downloading ? <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--ink-2)', borderTopColor: 'transparent' }} /> : <Download className="w-4 h-4" />}
                  <span className="hidden sm:inline">{downloading ? 'Generating...' : 'Download PDF'}</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    track('upgrade_clicked', { source: 'report_blur', cta: 'pdf_download_locked', audit_id: auditId });
                    setShowPDFUpsell(true);
                  }}
                  data-testid="download-pdf-btn"
                  className="uxa-submit uxa-submit--auto uxa-submit--compact uxa-submit--secondary"
                  title="Upgrade to download PDF reports"
                >
                  <Lock className="w-4 h-4" />
                  <span className="hidden sm:inline">Download PDF</span>
                </button>
              )}
              {/* Shown only when this report is not already paid for - `isPaid`
                  is per-audit for STARTER (audit.is_paid) and plan-wide for
                  PRO/MAX, so a paid report never offers to sell itself again.

                  It BUYS rather than navigates. This was a Link to
                  /account?tab=plans, which sent someone who wanted this report
                  unlocked to a plans page to work out which product that was -
                  while the locked "Download PDF" button two elements to the
                  left already opened the checkout popup directly. Same intent,
                  same page, two different journeys. Now both open the same
                  popup; only the analytics `cta` distinguishes them. */}
              {!isEntitled && (
                <button
                  type="button"
                  onClick={() => {
                    track('upgrade_clicked', { source: 'report_blur', cta: 'header_upgrade', audit_id: auditId });
                    setShowPDFUpsell(true);
                  }}
                  data-testid="upgrade-btn"
                  className="uxa-submit uxa-submit--auto uxa-submit--tertiary"
                >
                  <Crown className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Upgrade</span>
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </FadeInSection>

        {/* The "3 AI Lenses" banner was removed 2026-07-15: no such lenses
            existed in the pipeline. Nothing in this report may claim
            machinery that isn't real. */}

        {/* ===== OVERALL SCORE CARD ===== */}
        <FadeInSection data-section="overall_score" delay={0.1}>
          <div 
            className="rounded-3xl p-6 sm:p-10 mb-8" 
            style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}
            data-testid="overall-score-section"
          >
            <div className="text-center mb-8">
              {/* THE NUMBER IS PAGE 1's ON A MULTI-PAGE AUDIT, AND SAYS SO.
                  There is no site-level score by design: a checkout at 40 and a
                  homepage at 90 average to 65, and 65 describes nothing a
                  customer can act on. Page 1 is the anchor and pages 2 and 3
                  carry their own scores below.

                  Which makes the label load-bearing rather than decorative. An
                  unlabelled 61 sitting above three audited pages reads as a
                  verdict on the whole site — the same defect the mean was
                  rejected for, pointing the other way.

                  Unchanged for a single-page audit, so every report written
                  before multi-page reads exactly as it did. */}
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-6" style={{ color: 'var(--ink-2)' }} data-testid="score-label">{scoreLabel}</p>
              <ScoreRing score={overallScore} size={200} />
              <motion.p 
                className="text-lg font-bold mt-4" 
                style={{ color: getScoreColor(overallScore) }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
              >
                {/* Band label wins over the stored score_label. The column is
                    no longer written and is NOT dropped, but historical rows
                    hold labels from the old 80/65/50 ladder ("Critical
                    Issues"), which would contradict the band colour beside
                    them. Reading it first would reintroduce the exact
                    label/colour mismatch this table exists to remove. */}
                {getScoreLabel(overallScore)}
              </motion.p>
            </div>

            {/* ═══ TWO COLUMNS, LEFT-ALIGNED ═══════════════════════════════
                Everything under the ring used to be a stack of centred blocks
                at max-w-md / max-w-lg with mx-auto, which left a dead gutter
                down the right of a card that is 1180px wide, and set three
                paragraphs of body copy CENTRED. Centred prose is ragged on
                both edges; at this measure the reader hunts for the start of
                every line.

                The narrative and the scale now sit in a left column at a real
                measure, and the score arithmetic - which is the evidence FOR
                the number above, and was itself a centred box floating in the
                middle of the prose - sits beside them in the gutter that was
                empty. Nothing was added or removed; it is the same content on
                a grid.

                Stacks below lg. The derivation comes SECOND there, because on
                a narrow screen the reader wants the finding before the
                arithmetic. */}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-8 lg:gap-10 items-start mb-8">
              <div className="min-w-0">
                {/* SCOPE, ABOVE EVERYTHING ELSE. When a same-domain redirect
                    put the audit on a sign-in wall instead of the page that
                    was asked for, the reader has to know that before they
                    read a score, not after. A cross-domain redirect already
                    refuses; this is the same-domain case, which returns 200
                    and passes every other guard.

                    Not a warning colour: nothing went wrong and the findings
                    below are real. It is a statement of what was measured. */}
                {audit.scope_note && (
                  <p
                    className="uxa-dense mb-3"
                    style={{
                      color: 'var(--ink-1)',
                      background: 'var(--surface-sand-deep)',
                      border: '1px solid var(--border-1)',
                      borderRadius: '10px',
                      padding: '0.75rem 0.9rem',
                    }}
                    data-testid="scope-note"
                  >
                    {audit.scope_note}
                  </p>
                )}
                {/* PAGES COVERED, in the same place and for the same reason as
                    the scope note above: the reader has to know what the score
                    was measured on BEFORE they read it. A 2-of-3 audit whose
                    number looks like a verdict on the whole site is a silent
                    short-change at full price, which is the rule the Mobile UX
                    defect broke.

                    Amber, unlike the scope note, because something DID go
                    wrong here - a page we were asked for could not be reached.
                    The reason is named per page: "the capture failed" without
                    a cause is the gap mobile_capture_error was added to close.

                    Rendered for FREE viewers too. Coverage is not paid content;
                    it is the scope of what the number describes. */}
                {coverageNote && (
                  <div
                    className="mb-3"
                    style={{
                      background: '#FFFBEB',
                      border: '1px solid #FCD34D',
                      borderRadius: '10px',
                      padding: '0.75rem 0.9rem',
                    }}
                    data-testid="coverage-note"
                  >
                    <p className="uxa-dense" style={{ color: '#92400E', fontWeight: 700, marginBottom: 4 }}>
                      Pages covered
                    </p>
                    <p className="uxa-dense" style={{ color: '#78350F' }}>{coverageNote}</p>
                    {(audit.pages_failed || []).map((p) => (
                      <p key={p.url} className="uxa-dense" style={{ color: '#78350F', marginTop: 4 }}>
                        <strong>{p.url}</strong> — {p.reason}
                      </p>
                    ))}
                  </div>
                )}

                {/* A degraded audit that says so beats a lost one. Rendered for
                    FREE viewers too: what was stored is not paid content, it is
                    the scope of what the images below show. */}
                {captureNote && (
                  <div
                    className="mb-3"
                    style={{
                      background: '#FFFBEB',
                      border: '1px solid #FCD34D',
                      borderRadius: '10px',
                      padding: '0.75rem 0.9rem',
                    }}
                    data-testid="capture-note"
                  >
                    <p className="uxa-dense" style={{ color: '#92400E', fontWeight: 700, marginBottom: 4 }}>
                      How much was analysed
                    </p>
                    <p className="uxa-dense" style={{ color: '#78350F' }}>{captureNote}</p>
                  </div>
                )}

                {storageNote && (
                  <div
                    className="mb-3"
                    style={{
                      background: '#FFFBEB',
                      border: '1px solid #FCD34D',
                      borderRadius: '10px',
                      padding: '0.75rem 0.9rem',
                    }}
                    data-testid="storage-note"
                  >
                    <p className="uxa-dense" style={{ color: '#92400E', fontWeight: 700, marginBottom: 4 }}>
                      Screenshots stored
                    </p>
                    <p className="uxa-dense" style={{ color: '#78350F' }}>{storageNote}</p>
                  </div>
                )}
                {/* Purpose-fit line (Track 6, Phase 2): the page's classified
                    job and how the top finding bears on it. Empty for
                    "general" pages and pre-Phase-2 audits - renders nothing. */}
                {audit.purpose_fit && (
                  <p
                    className="uxa-dense font-semibold"
                    style={{ color: 'var(--ink-1)' }}
                    data-testid="purpose-fit-line"
                  >
                    {audit.purpose_fit.replace(/upload:\/\//g, '')}
                  </p>
                )}
                {audit.executive_summary && (
                  <p className="text-sm mt-3 leading-relaxed" style={{ color: 'var(--ink-2)' }}>
                    {audit.executive_summary.replace(/upload:\/\//g, '')}
                  </p>
                )}

                {/* The benchmark bar ("Industry Avg 62 / Top 10% 85+") was
                    removed 2026-07-15. Those numbers were hardcoded - no
                    benchmark dataset exists. A real benchmark computed from
                    actual audits is tracked as P1; until then the score
                    stands on its own evidence. */}
                <div className="mt-6" data-testid="score-scale">
                  <div className="relative h-3 rounded-full overflow-hidden" style={{ background: 'var(--surface-panel)' }}>
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: getScoreColor(overallScore) }}
                      initial={{ width: 0 }}
                      animate={{ width: `${overallScore}%` }}
                      transition={{ duration: 1.2, ease: 'easeOut' }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[10px] mt-2" style={{ color: 'var(--ink-2)' }}>
                    <span>0</span>
                    <span className="text-[11px] font-medium" style={{ color: 'var(--ink-2)' }}>Your Score: {overallScore}</span>
                    <span>100</span>
                  </div>
                </div>
              </div>

              {/* Score derivation (Phase 2). Lines are built server-side by
                  audit.scoring.derivation_lines so the PDF states identical
                  arithmetic. A severity ceiling, when it fires, arrives as its
                  own line. Absent on pre-Phase-2 audits - renders nothing. */}
              {Array.isArray(audit.score_derivation_lines) && audit.score_derivation_lines.length > 0 && (
                <div
                  className="rounded-xl px-4 py-3 text-left"
                  style={{ background: 'var(--surface-canvas)', border: '1px solid var(--border-1)' }}
                  data-testid="score-derivation"
                >
                  <p className="text-[10px] uppercase tracking-[0.12em] font-bold mb-2" style={{ color: 'var(--ink-2)' }}>
                    How this score was reached
                  </p>
                  {audit.score_derivation_lines.map((line, i) => (
                    <p
                      key={i}
                      className={`text-xs leading-relaxed ${i === audit.score_derivation_lines.length - 1 ? 'font-bold mt-1' : ''}`}
                      /* WAS #5A5A6A, which is not a token and is not any of the
                         four inks. --ink-2 is the secondary text colour and
                         measures 6.3:1 on this ground. */
                      style={{ color: i === audit.score_derivation_lines.length - 1 ? 'var(--ink-1)' : 'var(--ink-2)' }}
                    >
                      {line}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Severity counts only. Quick Fixes (an EFFORT axis) was removed
                from this row on 2026-07-16 (Track 4, item 4): mixing it with
                Critical/Major/Minor double-counted findings — the quick fixes
                are minors counted twice, so the row summed to more than the
                finding count. Effort lives in the roadmap where it belongs. */}
            {/* PHASE G: StatTile. These were three hand-rolled boxes on raw
                rgba fills — rgba(239,68,68) and rgba(245,158,11) are the
                Apollo-era red and amber, not the ruled severity values, so the
                counts were labelled in one palette and the chips beside them in
                another. The meters carry the ruled fills now. The totals are
                proportions of the finding count, which is a breakdown of the
                number above them and not a change over time — StatTile has no
                `delta` prop precisely so that distinction cannot blur. */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4" data-testid="stats-summary">
              <StatTile
                label="Critical" value={stats.critical} testId="stat-critical"
                meter={findingTotal ? (stats.critical / findingTotal) * 100 : 0}
                meterColor="var(--severity-critical-fill)"
              />
              <StatTile
                label="Major" value={stats.major} testId="stat-major"
                meter={findingTotal ? (stats.major / findingTotal) * 100 : 0}
                meterColor="var(--severity-major-text)"
              />
              <StatTile
                label="Minor" value={stats.minor} testId="stat-minor"
                meter={findingTotal ? (stats.minor / findingTotal) * 100 : 0}
                meterColor="var(--ink-3)"
              />
            </div>
          </div>
        </FadeInSection>

        {/* ═══ ACROSS YOUR PAGES ═══════════════════════════════════════════
            ABOVE the dimension cards, deliberately. This is the thing a
            three-page audit buys that three separate audits do not: a claim
            about the RELATION between pages. Putting it below the per-dimension
            grid, or folding it into one page's finding list, buries the only
            output no single-page competitor can produce.

            Every finding here names the pages it ranges over and the concrete
            difference. That is not a rendering choice — the backend validator
            drops anything it cannot ground in two audited pages, so a finding
            reaching this component has already been checked. What the component
            must not do is DISPLAY it in a way that loses the grounding, which
            is why the page list is rendered as chips rather than folded into
            prose that a reader can skim past.

            NAMED EVEN WHEN EMPTY (redesign Phase 3a). It used to render
            nothing at all, on the argument that a header over an empty list
            reads as a failure. That argument was wrong for the commonest
            case: a FREE audit is capped at one page
            (page_set.MAX_PAGES_FREE) and a comparison needs two, so the
            section is empty for a reason the reader would want to know, and
            hiding the section hid the reason with it. The empty state says
            what it needs; it does not apologise and it does not upsell. */}
        {/* ===== LEVERAGE ===== */}
        {/* Order (redesign Phase 3a): score row, leverage, cross-page,
            component groups. Leverage sits directly under the score because
            it is the first thing that tells a designer WHERE to work; a
            reader who stops after two sections should still have that.
            Server-derived, and null for a non-WEB artefact - the six page
            regions do not apply there, and an empty grouping would read as a
            measurement rather than as an absence of one. */}
        <FadeInSection data-section="leverage" delay={0.1}>
          <div className="mb-10">
            <LeverageLine leverage={audit?.leverage} />
          </div>
        </FadeInSection>

        <FadeInSection data-section="cross_page" delay={0.12}>
          <div className="mb-10" data-testid="cross-page-section">
            <h2 className="font-heading text-lg mb-1" style={{ color: 'var(--ink-1)' }}>
              Across your pages
            </h2>
            <p className="uxa-dense mb-5" style={{ color: 'var(--ink-2)' }}>
              What is true of the pages together, rather than of any one of them.
            </p>
            {crossPageFindings.length === 0 ? (
              <CrossPageEmpty pageCount={Math.max(1, pagesRequested.length || (extraPages.length + 1))} />
            ) : (
              /* LIST ROWS, NOT CARDS. Eight of these as full cards measured
                 2408px at 390 and 1461px at 1440 - 29.3% and 27.2% of the
                 whole report, for a section that is not the findings section.
                 Same treatment the locked row got: readable in one line,
                 opened when it is the one you want.

                 <details> rather than state - see the note on .uxa-xp-row in
                 app-states.css. Nothing is removed: every field the card
                 showed is still here, one disclosure away, and all of it is
                 in the DOM whether the row is open or not. */
              <div>
                {crossPageFindings.map((f, i) => (
                  <details
                    key={`${f.title}-${i}`}
                    className="uxa-xp-row"
                    data-testid={`cross-page-finding-${i}`}
                  >
                    <summary className="uxa-xp-summary">
                      <SeverityBadge severity={f.severity} />
                      <span className="uxa-xp-title">{f.title}</span>
                      <span className="uxa-xp-count">
                        {(f.pages || []).length} pages
                      </span>
                      <ChevronRight className="w-4 h-4 uxa-xp-chevron" aria-hidden="true" />
                    </summary>

                    <div className="uxa-xp-body">
                      {/* The pages, as chips. A cross-page finding cannot cite an
                          element the way a page finding does — its evidence IS
                          the set it compares, so that set has to be visible
                          rather than buried in the sentence. */}
                      <div className="flex flex-wrap gap-1.5" data-testid={`cross-page-pages-${i}`}>
                        {(f.pages || []).map((url) => (
                          <span
                            key={url}
                            title={url}
                            className="text-[11px] px-2 py-0.5 rounded-full"
                            style={{
                              background: url === f.focus ? 'var(--surface-sand-deep)' : 'var(--surface-panel)',
                              border: '1px solid var(--border-1)',
                              color: 'var(--ink-2)',
                              fontWeight: url === f.focus ? 700 : 400,
                            }}
                          >
                            {shortPath(url)}
                          </span>
                        ))}
                      </div>

                      {f.evidence && (
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-2)' }}>
                          {f.evidence}
                        </p>
                      )}
                      {f.recommendation && (
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-1)' }}>
                          <strong>Fix:</strong> {f.recommendation}
                        </p>
                      )}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        </FadeInSection>

        {/* ===== DIMENSION SCORES ===== */}
        <FadeInSection data-section="dimension_scores" delay={0.15}>
          <h2 className="font-heading text-lg mb-5" style={{ color: 'var(--ink-1)' }}>Dimension Scores</h2>
          {/* PHASE G: DimensionBars, the at-a-glance strip the spec's data slide
              draws. It repeats numbers the cards below also show, deliberately:
              six scores side by side answer "which is worst" in one look, which
              a three-column card grid does not.
              IT ALSO SHOWS ALL SIX. The cards render only dimensions that were
              assessed, so an unscored dimension simply vanishes and the reader
              cannot tell it from one that was never part of the product. The
              strip draws an empty track and says "not scored" — absent is not
              zero and neither is invisible. */}
          <div className="rounded-2xl p-5 mb-6" style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}>
            <DimensionBars
              scores={Object.fromEntries(DIM_KEY_BY_CARD.map(([card, key]) => [card, audit[key]]))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* EVERY DIMENSION RENDERS. THE UNSCORED ONES SAY SO.
                WAS: `.filter(([, dim]) => audit[dim.key] != null)` — an
                unassessed dimension had no card at all, so the reader could not
                tell a dimension this run could not score from one the product
                does not have. The strip above already refused that equivalence
                ("absent is not zero and neither is invisible"); the cards below
                it still made it, which is the divergence.
                The card keeps its shell, its icon and its name, and drops
                everything that would state a value: no radial, no fill, a
                hatched track, and the words. Same phrase and same hatch as
                CompareAuditsPage's DimensionRow — one vocabulary for "we did
                not measure this", across both surfaces that can hit it. */}
            {Object.entries(dimMeta)
              /* THE FLAG NOW GATES THE RENDER, which is what it always claimed
                 to do. `legacyOnly` was declared on the `onboarding` entry and
                 read NOWHERE, so every report iterated all seven and drew
                 "Onboarding — Not assessed" for a dimension retired on
                 2026-07-15 and stripped from eight surfaces in 97bec2c.

                 A retired dimension named on a delivered report is the exact
                 thing that removal was for. The card survives for a
                 PRE-CUTOFF audit that really stored an onboard_score, because
                 rewriting history inside a report someone already received is
                 worse than showing a legacy field - which is the reasoning the
                 six-dimensions waiver has always given, and now the reasoning
                 the code follows.

                 The condition is deliberately "has a score", not "is old": the
                 date is not on the audit and the score is, so the flag asks
                 the question the data can answer. */
              .filter(([, dim]) => !dim.legacyOnly
                || (audit[dim.key] !== null && audit[dim.key] !== undefined))
              .map(([key, dim], i) => {
              const assessed = audit[dim.key] !== null && audit[dim.key] !== undefined;
              const score = audit[dim.key] || 0;
              const Icon = dim.icon;
              return (
                <motion.div 
                  key={key} 
                  initial={{ opacity: 0, y: 15 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: 0.2 + i * 0.06 }}
                  className="rounded-2xl p-5" 
                  /* The score-band wash is a reading of the score. There is no
                     score, so there is no wash — a card tinted at 0 reads as a
                     catastrophic result rather than as a blank. */
                  style={{ background: assessed ? getScoreBgGradient(score) : 'var(--surface-white)', border: '1px solid var(--border-1)' }}
                  data-testid={`dimension-${key}`}
                  data-assessed={assessed ? 'true' : 'false'}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ background: 'var(--surface-panel)' }}
                      >
                        <Icon className="w-5 h-5" style={{ color: dim.color }} />
                      </div>
                      <div>
                        <h3 className="font-sans font-bold text-sm" style={{ color: 'var(--ink-1)' }}>{dim.label}</h3>
                        <p className="text-[10px] mt-0.5" style={{ color: 'var(--ink-2)' }}>{dim.desc}</p>
                      </div>
                    </div>
                    {assessed
                      ? <MiniRadialScore score={score} size={48} color={getScoreColor(score)} />
                      : <span className="font-sans font-bold text-xs whitespace-nowrap" style={{ color: 'var(--ink-2)' }}>Not assessed</span>}
                  </div>
                  <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: 'var(--surface-panel)' }}>
                    {assessed
                      ? <motion.div 
                          className="h-full rounded-full" 
                          style={{ backgroundColor: getScoreColor(score) }}
                          initial={{ width: 0 }} 
                          animate={{ width: `${score}%` }} 
                          transition={{ duration: 1, delay: 0.3 + i * 0.05 }} 
                        />
                      /* Hatched, never a zero-width fill: an empty track and a
                         zero score must not look the same. */
                      : <div className="uxa-track-absent" />}
                  </div>
                  {/* Dimension justification: FREE users get a sample of the
                      assessments (Navigation + Visual Hierarchy) — enough to
                      prove the site was genuinely measured — while the rest are
                      part of the paid report. Scores + bars always show; only
                      the paragraph is gated. Full text, no line-clamp — the
                      summary was truncating mid-sentence while the PDF showed it
                      in full (Track 4, item 6). */}
                  {/* An unassessed dimension gets the reason, not the paywall.
                      There is no analysis behind the lock to sell, and
                      "Full analysis in the paid report" over a dimension the
                      paid report also cannot score is a promise we would not
                      keep. The reason is stated in the terms the validator
                      actually uses: it removes a dimension when the run carried
                      no evidence for it (backend/audit/validator.py:708). */}
                  {!assessed ? (
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-2)' }}>
                      This run carried no evidence for this dimension, so it was not scored. It is not counted in the overall score.
                    </p>
                  ) : (isEntitled || FREE_DIMENSION_JUSTIFICATIONS.includes(key)) ? (
                    summaries[key] && <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-2)' }}>{summaries[key]}</p>
                  ) : (
                    <div className="flex flex-col gap-1 text-xs" style={{ color: 'var(--ink-2)' }}>
                      {audit.dimension_issue_counts?.[key] > 0 && (
                        <span className="font-medium" style={{ color: 'var(--ink-2)' }}>
                          {audit.dimension_issue_counts[key]} issue{audit.dimension_issue_counts[key] !== 1 ? 's' : ''} measured in this dimension
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <Lock className="w-3 h-3" />
                        Full analysis in the paid report
                      </span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
          
          {/* Partial Score Reveal - for unentitled viewers only */}
          {!isEntitled && (
            <div className="mb-10">
              <PartialScoreReveal 
                lowestDim={Object.entries(dimMeta).reduce((lowest, [key, dim]) => {
                  const score = audit[dim.key];
                  if (score === null || score === undefined) return lowest;
                  if (!lowest || score < lowest.score) return { name: dim.label, score, key };
                  return lowest;
                }, null)?.name || 'Accessibility'}
                lowestScore={Math.min(...Object.values(dimMeta)
                  .map(dim => audit[dim.key])
                  .filter(s => s !== null && s !== undefined))}
                onUnlock={() => {
                  track('upgrade_clicked', { source: 'report_blur', cta: 'partial_score_reveal', audit_id: auditId });
                  setShowPDFUpsell(true);
                }}
              />
            </div>
          )}
        </FadeInSection>

        {/* ═══ THE OTHER PAGES ═════════════════════════════════════════════
            AFTER the dimension cards, because those cards are PAGE 1's and the
            reader has to have finished with page 1 before another page's
            numbers appear. Interleaving them would leave three sets of six
            scores on one screen with nothing saying which belongs to what.

            Each page carries its OWN score, named. There is no site-level
            number by design — a checkout at 40 and a homepage at 90 average to
            65, which describes nothing a customer can act on and hides the
            checkout. So the reader gets the real numbers, one per page,
            attributed.

            A page whose analysis failed shows the REASON rather than being
            dropped from the list. Dropping it would make a three-page audit
            look like a two-page one, which is the silent short-change this
            phase exists to prevent. `analysis_error` is written by the pipeline
            precisely so this row has something true to say. */}
        {extraPages.length > 0 && (
          <FadeInSection data-section="per_page" delay={0.2}>
            <div className="mb-10" data-testid="per-page-section">
              <h2 className="font-heading text-lg mb-1" style={{ color: 'var(--ink-1)' }}>
                The other pages
              </h2>
              <p className="uxa-dense mb-5" style={{ color: 'var(--ink-2)' }}>
                Each page is scored on its own. There is no combined number — averaging a
                strong page with a weak one describes neither.
              </p>
              <div className="flex flex-col gap-4">
                {extraPages.map((page, i) => {
                  const failed = !!page.analysis_error || page.overall_score === null
                    || page.overall_score === undefined;
                  return (
                    <div
                      key={page.url || i}
                      className="rounded-2xl p-5"
                      style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}
                      data-testid={`page-section-${i + 2}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1"
                             style={{ color: 'var(--ink-2)' }}>
                            Page {i + 2}
                          </p>
                          <p className="font-sans font-bold text-sm break-all"
                             style={{ color: 'var(--ink-1)' }}>
                            {page.url}
                          </p>
                        </div>
                        {failed ? (
                          <span className="text-xs whitespace-nowrap" style={{ color: 'var(--ink-2)' }}>
                            Not scored
                          </span>
                        ) : (
                          <MiniRadialScore score={page.overall_score} size={48}
                                           color={getScoreColor(page.overall_score)} />
                        )}
                      </div>

                      {/* THE SCORE ABOVE NEVER TRAVELS ALONE. A page analysed
                          from part of its height sat beside two whole ones at
                          47, 48 and 50 with nothing to separate them; a reader
                          concluded it was slightly worse, when most of it was
                          simply not looked at. */}
                      {captureNoteOf(page) && (
                        <p className="uxa-dense mb-3" style={{ color: '#8A4A32' }}
                           data-testid={`page-capture-note-${i + 2}`}>
                          {captureNoteOf(page)}
                        </p>
                      )}

                      {failed ? (
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-2)' }}
                           data-testid={`page-error-${i + 2}`}>
                          {page.analysis_error
                            ? `This page was captured but could not be analysed: ${page.analysis_error}`
                            : 'This page could not be analysed, so it carries no score.'}
                        </p>
                      ) : (
                        <>
                          {page.summary && (
                            <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--ink-2)' }}>
                              {page.summary}
                            </p>
                          )}
                          {/* Dimension scores for THIS page. `!= null` and not
                              a truthiness check: a genuine 0 is a score and
                              must render, while an unassessed dimension is
                              null and must not be drawn as one. */}
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(page.dimensions || {})
                              .filter(([, v]) => v !== null && v !== undefined)
                              .map(([key, value]) => (
                                <span
                                  key={key}
                                  className="text-[11px] px-2 py-1 rounded-lg"
                                  style={{
                                    background: 'var(--surface-panel)',
                                    border: '1px solid var(--border-1)',
                                    color: 'var(--ink-1)',
                                  }}
                                >
                                  {DIMENSION_LABELS[key] || key}{' '}
                                  <strong style={{ color: getScoreColor(value) }}>{value}</strong>
                                </span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </FadeInSection>
        )}

        {/* ===== ANNOTATED SCREENSHOTS ===== */}
        {screenshots.length > 0 && (
          <FadeInSection data-section="annotated_screenshots" delay={0.2}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg" style={{ color: 'var(--ink-1)' }}>Annotated Screenshots</h2>
              {/* THE SCREENSHOT SELECTOR IS GONE, and its reason went with it.
                  It existed because one image was shown at a time and the
                  reader needed to switch between them. Its comment recorded the
                  constraint that made it awkward: "the API returns an array and
                  NOTHING in the response says which capture is which".

                  Both halves are now answered. Each finding shows the capture
                  its own evidence came from, labelled Desktop 1440px or Mobile
                  390px, so there is nothing to switch between and nothing left
                  to infer from index order. */}
            </div>
            
            {/* ═══ ONE FINDING, ONE CAPTURE, SIDE BY SIDE ══════════════════
                See the captureFindings comment above for why this is rows
                rather than six markers on one image: the mobile-sourced
                markers were being drawn on the desktop screenshot. */}
            {isEntitled ? (
              <div className="flex flex-col gap-4 mb-10" data-testid="capture-findings">
                {captureFindings.map(({ finding, num, src, pos, useMobile, wantsMobile }) => {
                  const sevColor = finding.severity === 'critical'
                    ? 'var(--danger)'
                    : finding.severity === 'major' ? 'var(--severity-major-text)' : 'var(--ink-2)';
                  return (
                    <div
                      key={num}
                      className="rounded-2xl p-4"
                      style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}
                      data-testid={`capture-finding-${num}`}
                      data-viewport={useMobile ? 'mobile' : 'desktop'}
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4 lg:gap-6 items-start">
                        {/* ── The capture this finding's evidence came from ── */}
                        <div
                          className="relative rounded-xl overflow-hidden"
                          style={{ background: 'var(--surface-panel)' }}
                        >
                          <img
                            src={src}
                            onError={refreshScreenshots}
                            alt={`${useMobile ? 'Mobile' : 'Desktop'} capture for finding ${num}: ${finding.title}`}
                            className="w-full object-contain max-h-[420px] rounded-lg"
                          />
                          {pos && (
                            /* 44x44 target, 32px dot. One marker, not six. */
                            <span
                              className="absolute flex items-center justify-center"
                              style={{
                                top: pos.top,
                                left: pos.left,
                                transform: 'translate(-50%, -50%)',
                                width: '44px',
                                height: '44px',
                              }}
                              aria-hidden="true"
                              data-testid={`capture-marker-${num}`}
                            >
                              <span
                                className="rounded-full flex items-center justify-center text-xs font-bold"
                                style={{
                                  width: '32px', height: '32px',
                                  color: '#FFFFFF', backgroundColor: sevColor,
                                  boxShadow: '0 0 0 3px rgba(255,255,255,0.9)',
                                }}
                              >
                                {num}
                              </span>
                            </span>
                          )}
                          <span
                            className="absolute text-[10px] font-bold uppercase tracking-[0.1em] rounded-full px-2 py-1"
                            style={{
                              top: '10px', left: '10px',
                              background: 'var(--surface-white)',
                              color: 'var(--ink-2)',
                              border: '1px solid var(--border-2)',
                            }}
                          >
                            {useMobile ? 'Mobile 390px' : 'Desktop 1440px'}
                          </span>
                        </div>

                        {/* ── Its explanation ──────────────────────────────── */}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span
                              className="flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0"
                              style={{
                                width: '24px', height: '24px',
                                color: '#FFFFFF', backgroundColor: sevColor,
                              }}
                              aria-hidden="true"
                            >
                              {num}
                            </span>
                            <SeverityBadge severity={finding.severity} />
                            {finding.annotation?.ui_element && (
                              <span
                                className="text-[10px] px-2 py-0.5 rounded-full"
                                style={{ background: 'var(--surface-panel)', color: 'var(--ink-2)' }}
                              >
                                {finding.annotation.ui_element}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-semibold mb-1" style={{ color: 'var(--ink-1)' }}>
                            {finding.title}
                          </p>
                          {finding.description && (
                            <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-2)' }}>
                              {finding.description}
                            </p>
                          )}
                          {/* Said plainly rather than left as a silent gap. A
                              marker in the wrong place is worse than none. */}
                          {!pos && (
                            <p className="text-[11px] mt-2" style={{ color: 'var(--ink-3)' }}>
                              {wantsMobile && !useMobile
                                ? 'No mobile capture was stored for this audit.'
                                : 'This finding was not pinned to a point on the capture.'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div 
                className="relative rounded-2xl p-4 mb-10 overflow-hidden"
                style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}
              >
                <div
                  className="absolute inset-0 z-10 flex flex-col items-center justify-center"
                  style={{ background: 'rgba(17,24,39,0.55)' }}
                >
                  <Lock className="w-10 h-10 mb-3" style={{ color: '#F9FAFB' }} />
                  <p className="text-sm font-semibold mb-1" style={{ color: '#F9FAFB' }}>Annotated Screenshots</p>
                  <p className="text-xs mb-3" style={{ color: '#E2E8F0' }}>Available on the paid report</p>
                  {/* The third and last "buy this report" affordance. All three
                      now open the same popup; none navigates to a plans page. */}
                  <button
                    type="button"
                    data-testid="screenshots-locked-upgrade"
                    onClick={() => {
                      track('upgrade_clicked', { source: 'report_blur', cta: 'screenshots_locked', audit_id: auditId });
                      setShowPDFUpsell(true);
                    }}
                    className="uxa-submit uxa-submit--auto uxa-submit--tertiary uxa-submit--on-dark"
                  >
                    <Crown className="w-4 h-4" aria-hidden="true" />
                    Upgrade
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
                {/* Real annotated page, downscaled + blurred server-side (D4):
                    the buyer sees WHAT they are buying, never readable content. */}
                <div className="h-64 rounded-xl overflow-hidden" style={{ background: 'var(--surface-panel)' }}>
                  {screenshots[0] && (
                    <img
                      src={screenshots[0]}
                      onError={refreshScreenshots}
                      alt="Blurred annotated screenshot preview"
                      className="w-full h-full object-cover object-top"
                    />
                  )}
                </div>
              </div>
            )}
          </FadeInSection>
        )}

        {/* ===== QUICK WINS ===== */}
        {quickWins.length > 0 && (
          <FadeInSection data-section="quick_wins" delay={0.25}>
            <div 
              className="rounded-2xl p-6 mb-10" 
              style={{ background: opaque('rgba(16,185,129,0.1)'), border: '1px solid rgba(16,185,129,0.25)' }}
              data-testid="quick-wins"
            >
              <div className="flex items-center gap-3 mb-5">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.2)' }}
                >
                  <Zap className="w-5 h-5" style={{ color: 'var(--success)' }} />
                </div>
                <div>
                  <h3 className="font-sans font-bold text-base" style={{ color: 'var(--success)' }}>Fix These First</h3>
                  <p className="text-xs" style={{ color: 'rgba(16,185,129,0.7)' }}>Highest impact, lowest effort improvements</p>
                </div>
              </div>
              <div className="space-y-3">
                {quickWins.map((f, i) => {
                  // Free users only see first item fully, rest are blurred
                  const isLocked = !isEntitled && i > 0;
                  
                  if (isLocked) {
                    return (
                      <div key={i} className="uxa-locked-row">
                        <span className="uxa-stage-mark" style={{ borderColor: 'var(--border-2)' }}>{i + 1}</span>
                        <span className="flex-1 min-w-0">
                          <span className="uxa-file-name" style={{ display: 'block' }}>{f.title}</span>
                          <span className="uxa-stage-note capitalize">
                            {f.screen ? <span className="uxa-screen-tag">{f.screen}</span> : null}
                            {f.dimension?.replace('_', ' ')}
                          </span>
                        </span>
                        <span className="uxa-finding-tag uxa-finding-tag--locked">Fix locked</span>
                        <span className="uxa-locked-row-lock" aria-label="Locked"><Lock className="w-3.5 h-3.5" /></span>
                      </div>
                    );
                  }
                  
                  return (
                    <motion.div 
                      key={i} 
                      initial={{ opacity: 0, x: -10 }} 
                      animate={{ opacity: 1, x: 0 }} 
                      transition={{ delay: 0.3 + i * 0.1 }}
                      className="flex items-center gap-4 p-4 rounded-xl"
                      style={{ background: 'var(--surface-white)', border: '1px solid rgba(16,185,129,0.2)' }}
                    >
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: 'var(--success)' }}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: 'var(--ink-1)' }}>{f.title}</p>
                        <p className="text-[11px] capitalize" style={{ color: 'var(--ink-2)' }}>
                          {f.screen ? <span className="uxa-screen-tag">{f.screen}</span> : null}
                          {f.dimension?.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span 
                          className="text-[10px] px-2.5 py-1 rounded-full font-semibold capitalize"
                          style={{ background: 'rgba(16,185,129,0.2)', color: 'var(--success)' }}
                        >
                          {f.effort || 'Low'} effort
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </FadeInSection>
        )}

        {/* ===== ACROSS SCREENS ===== */}
        {consistency.length > 0 && (
          <FadeInSection data-section="across_screens" delay={0.25}>
            <div className="uxa-consistency" data-testid="consistency-findings">
              <div className="mb-4">
                <h2 className="font-heading text-lg" style={{ color: 'var(--ink-1)' }}>
                  Across Screens ({consistency.length})
                </h2>
                <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
                  Places the design disagrees with itself. Not visible from any one screen.
                </p>
              </div>
              <ul className="uxa-consistency-list">
                {consistency.map((c, i) => {
                  /* A LOCKED entry carries severity and its screen names and
                     no prose at all: free_view._teaser_consistency strips
                     observation, user_impact and recommended_fix on the
                     SERVER, so `c.locked` is the server's answer and not a
                     presentational guess. Rendering the open row for it would
                     print three empty paragraphs, which reads as broken
                     rather than as withheld.

                     `locked` is read rather than recomputed from isPaid: the
                     entitlement decision is the API's, and a client that
                     decides for itself is the CSS paywall this file already
                     removed once. */
                  if (c.locked) {
                    return (
                      <li key={i} className="uxa-consistency-item">
                        <div className="uxa-locked-row">
                          <span className="flex-1 min-w-0">
                            <span className="flex items-center gap-2 flex-wrap">
                              <SeverityBadge severity={String(c.severity || 'minor').toLowerCase()} />
                              {(c.screens || []).map(name => (
                                <span key={name} className="uxa-screen-tag">{name}</span>
                              ))}
                            </span>
                          </span>
                          <span className="uxa-finding-tag uxa-finding-tag--locked">Disagreement locked</span>
                          <span className="uxa-locked-row-lock" aria-label="Locked"><Lock className="w-3.5 h-3.5" /></span>
                        </div>
                      </li>
                    );
                  }
                  return (
                    <li key={i} className="uxa-consistency-item">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <SeverityBadge severity={String(c.severity || 'minor').toLowerCase()} />
                        {(c.screens || []).map(name => (
                          <span key={name} className="uxa-screen-tag">{name}</span>
                        ))}
                      </div>
                      <p style={{ color: 'var(--ink-1)' }}>{c.observation}</p>
                      {c.user_impact && (
                        <p className="text-sm mt-1.5" style={{ color: 'var(--ink-2)' }}>{c.user_impact}</p>
                      )}
                      {c.recommended_fix && (
                        <p className="text-sm mt-2" style={{ color: 'var(--ink-2)' }}>
                          <span className="font-semibold" style={{ color: 'var(--ink-1)' }}>Fix: </span>
                          {c.recommended_fix}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </FadeInSection>
        )}

        {/* ===== DETAILED FINDINGS ===== */}
        <FadeInSection data-section="detailed_findings" delay={0.3}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg" style={{ color: 'var(--ink-1)' }}>Detailed Findings ({findings.length})</h2>
            {isEntitled && (
              <button 
                onClick={() => {
                  const allExpanded = findings.every((_, i) => expandedFindings[i]);
                  const newState = {};
                  findings.forEach((_, i) => { newState[i] = !allExpanded; });
                  setExpandedFindings(newState);
                }}
                className="text-xs transition-colors flex items-center gap-1"
                style={{ color: 'var(--ink-2)' }}
              >
                {findings.every((_, i) => expandedFindings[i]) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {findings.every((_, i) => expandedFindings[i]) ? 'Collapse All' : 'Expand All'}
              </button>
            )}
          </div>

          {/* The filter narrows the same spine; it does not split it. */}
          <div className="mb-5">
            <ViewportFilter counts={viewportChipCounts} value={viewport} onChange={setViewport} />
          </div>

          {/* ── THE SPINE: COMPONENT GROUPS ──────────────────────────────
              Grouped by the part of the page, which is what a designer
              opens. NOT by component+dimension - that is the tighter key the
              LEVERAGE PANEL counts, and the two answering differently is
              deliberate. reportGroups.js carries the argument.

              A group's count is the number of findings IN IT, and a finding
              in two components is in two groups, so these do not sum to the
              finding total. The total is printed once, in the heading above,
              from `findings.length`.

              An artefact with no components at all - every Figma or upload
              audit, where the six page regions do not apply - yields no
              groups and falls through to `ungrouped`, which renders as a
              plain list rather than an error or an empty state. */}
          <div className="space-y-6 mb-6" data-section="component_groups">
            {visibleGroups.map((group) => (
              <section
                key={group.key}
                id={`component-group-${group.key}`}
                data-testid={`component-group-${group.key}`}
              >
                <div className="flex items-baseline gap-2 mb-3">
                  <h3 className="font-heading text-base" style={{ color: 'var(--ink-1)' }}>
                    {group.label}
                  </h3>
                  <span
                    className="text-xs tabular-nums px-2 py-0.5 rounded-full"
                    style={{ background: 'var(--surface-sand-deep)', color: 'var(--ink-2)' }}
                    data-testid={`component-group-count-${group.key}`}
                  >
                    {group.members.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {/* The count above is the FULL membership; this list renders
                      the primary member in full and every other as a
                      reference row. The two are deliberately different
                      numbers and the count is the honest one - a group of
                      four that shows one card and three pointers still holds
                      four findings. */}
                  {group.members.map((m) => (m.isPrimary
                    ? renderFinding(m.finding, m.index)
                    : (
                      <FindingReference
                        key={`ref-${group.key}-${m.index}`}
                        /* Entitlement, not `locked`: one value for every
                           row on the page, from the server. See
                           referenceHeadline in reportGroups.js. */
                        headline={referenceHeadline(m.finding, isEntitled)}
                        destinationKey={m.primaryKey}
                        destinationLabel={m.primaryLabel}
                      />
                    )))}
                </div>
              </section>
            ))}

            {visibleUngrouped.length > 0 && (
              <section data-testid="component-group-ungrouped">
                {visibleGroups.length > 0 && (
                  <div className="flex items-baseline gap-2 mb-3">
                    <h3 className="font-heading text-base" style={{ color: 'var(--ink-1)' }}>
                      Not placed on the page
                    </h3>
                    <span
                      className="text-xs tabular-nums px-2 py-0.5 rounded-full"
                      style={{ background: 'var(--surface-sand-deep)', color: 'var(--ink-2)' }}
                    >
                      {visibleUngrouped.length}
                    </span>
                  </div>
                )}
                <div className="space-y-3">
                  {visibleUngrouped.map(({ finding, index }) => renderFinding(finding, index))}
                </div>
              </section>
            )}

            {/* The components whose findings all render elsewhere. One line,
                not a section: the section would be a heading, a count and N
                pointers away from itself, which on real data read as broken
                findings rather than as navigation. The count survives here so
                the membership is stated rather than deleted. */}
            {absorbedLine && (
              <p
                className="text-xs"
                style={{ color: 'var(--ink-3)' }}
                data-testid="absorbed-components"
              >
                Also spans {absorbedLine}.
              </p>
            )}

            {visibleGroups.length === 0 && visibleUngrouped.length === 0 && (
              <p className="text-sm" style={{ color: 'var(--ink-2)' }} data-testid="viewport-filter-empty">
                No findings at this viewport. Switch the filter above to see the rest.
              </p>
            )}
          </div>

          {/* ── THE BANNER NEEDS BOTH SIGNALS TO AGREE ────────────────────
              `!isEntitled` is the server's entitlement decision, and
              `lockedCount > 0` is the observable consequence of it - the
              trim actually having run over this payload. Either alone was
              enough to draw a $49 CTA over nine fully open findings, which
              is what shipped: the old condition was `!isPaid` and nothing
              looked at whether a single finding was locked.

              Requiring both means a disagreement renders NOTHING rather than
              a paywall nobody can act on. The banner is an upsell; the
              failure mode of showing it wrongly is worse than the failure
              mode of missing it. */}
          {!isEntitled && lockedCount > 0 && findings.length > 1 && (
            <UpgradeBanner
              openCount={openFindingCount(findings)}
              total={findings.length}
              criticalCount={stats.critical}
              price={PLAN_PRICES.STARTER.usd}
              lowCount={audit.low_finding_count || findings.length <= 2}
              onUpgrade={() => setShowPDFUpsell(true)}
            />
          )}
        </FadeInSection>

        {/* ===== SCORE INTERPRETATION ===== */}
        <FadeInSection data-section="score_interpretation" delay={0.35}>
          <div 
            className="rounded-2xl p-6 mb-10" 
            style={{ background: 'var(--surface-white)', border: '1px solid var(--border-1)' }}
            data-testid="score-guide"
          >
            <h3 className="font-heading text-base mb-4 flex items-center gap-2" style={{ color: 'var(--ink-1)' }}>
              <FileText className="w-5 h-5" style={{ color: 'var(--ink-2)' }} />
              Score Interpretation Guide
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { range: '80-100', label: 'Excellent', desc: 'Industry-leading UX', color: 'var(--success)' },
                { range: '60-79', label: 'Good', desc: 'Above average UX', color: 'var(--severity-major-text)' },
                { range: '40-59', label: 'Needs Work', desc: 'Notable issues to fix', color: 'var(--accent-text)' },
                { range: '0-39', label: 'Critical', desc: 'Requires immediate attention', color: 'var(--danger)' },
              ].map((tier, i) => (
                <div key={i} className="rounded-xl p-4" style={{ background: 'var(--surface-panel)', border: '1px solid var(--border-1)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: tier.color }} />
                    <span className="text-sm font-bold" style={{ color: 'var(--ink-1)' }}>{tier.range}</span>
                  </div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--ink-2)' }}>{tier.label}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--ink-2)' }}>{tier.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </FadeInSection>

        {/* ===== FULL-WIDTH DARK CTA ===== */}
        <FadeInSection data-section="cta" delay={0.4}>
          <div 
            className="rounded-3xl p-8 sm:p-12 text-center overflow-hidden relative" 
            style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, var(--surface-panel) 100%)', border: '1px solid var(--border-2)' }}
            data-testid="cta-section"
          >
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImRvdHMiIHg9IjAiIHk9IjAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNkb3RzKSIvPjwvc3ZnPg==')] opacity-50" />
            <div className="relative z-10">
              <h3 className="font-heading text-2xl sm:text-3xl mb-3" style={{ color: 'var(--ink-1)' }}>Want these issues fixed?</h3>
              <p className="text-sm mb-8 max-w-lg mx-auto" style={{ color: 'var(--ink-2)' }}>Our product development team can implement every recommendation in this report. Ship faster with expert help.</p>
              <a 
                href="https://meet.roarsinc.com/team/uxauditpro"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-book-call"
                className="uxa-submit uxa-submit--auto"
              >
                Book a Free Call
                <span className="uxa-submit-knob" aria-hidden="true"><ExternalLink className="w-4 h-4" /></span>
              </a>
            </div>
          </div>
        </FadeInSection>
      </div>

      {/* ===== MINIMAL FOOTER ===== */}
      <div data-section="footer" className="max-w-5xl mx-auto px-6 mt-8">
        <div className="py-4 flex flex-col sm:flex-row justify-center items-center gap-2" style={{ borderTop: '1px solid var(--border-1)' }}>
          {/* ── THE WHITE-LABEL FOOTER IS GONE FROM THIS PAGE, 2026-08-20 ──
              Owner's ruling, made after being shown what it costs: this branch
              rendered the agency logo and "Prepared by <agency>" and was the
              white-label add-on's only visible output on the web report. The
              same footer now shows for everyone.

              WHAT THE ADD-ON STILL DOES, so nobody reads this as it being
              dead: whiteLabelSettings still names the downloaded PDF
              (`${agency}-${app}.pdf`, see handleDownloadPDF) and the backend
              still applies branding to the PDF itself. The web report is what
              changed, not the product.

              If the add-on should render something else here instead, that is
              a design decision and not a leftover. */}
          {/* ONE attribution. "Powered by UXAuditPro" sat on the right of this
              row saying the same thing as "Generated by UXAuditPro.com" on the
              left - it was the survivor of the white-label branch that used to
              hold the left slot, not a second thing worth saying. */}
          <p className="text-[11px]" style={{ color: 'var(--ink-2)' }}>Generated by <span className="font-semibold" style={{ color: 'var(--accent-text)' }}>UXAuditPro.com</span></p>
        </div>
      </div>

      {/* ===== STICKY BOTTOM BAR ===== */}
      <motion.div data-section="sticky_bar" 
        initial={{ y: 100 }} 
        animate={{ y: 0 }} 
        transition={{ delay: 0.5 }}
        className="fixed bottom-0 left-0 right-0 py-3 px-4 sm:px-6 z-40" 
        style={{
          /* Opaque + shadow, never backdrop-filter: a translucent blurred bar
             forms its own stacking context over the transform-animated cards
             above it, which is the unresolved G1 defect. See tokens.css. */
          background: 'var(--ink-strong)',
          borderTop: '1px solid rgba(255,255,255,0.10)',
          boxShadow: 'var(--shadow-sticky)'
        }}
        data-testid="sticky-bar"
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${getScoreColor(overallScore)}25` }}>
              <span className="text-sm font-bold" style={{ color: getScoreColor(overallScore) }}>{overallScore}</span>
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold" style={{ color: '#F9FAFB' }}>{audit.app_name}</p>
              <p className="text-[10px]" style={{ color: '#94A3B8' }}>{findings.length} findings · {stats.critical} critical</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              data-testid="sticky-download"
              /* The SAME lockup as "Book a Free Call" below - pill, label,
                 trailing disc - inverted for this bar's --ink-strong ground by
                 --on-dark, which is the token system's own inverse and not a
                 second skin. --compact because a sticky bar is chrome. */
              className={`uxa-submit uxa-submit--auto uxa-submit--compact uxa-submit--on-dark${isEntitled ? '' : ' uxa-submit--attention'}`}
            >
              <span className="hidden sm:inline">Download</span> PDF
              {/* THE LOCK IS NOT DECORATION. For a free user this button does
                  not download anything - handleDownloadPDF opens the checkout
                  popup. The header's copy of this button has carried a lock
                  since it was built; this one said "Download PDF" and gave no
                  sign it was gated. Highlighting a control that does not do
                  what it says would have made that worse, not better. */}
              <span className="uxa-submit-knob" aria-hidden="true">
                {isEntitled ? <Download className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              </span>
            </button>
            <a
              href="https://meet.roarsinc.com/team/uxauditpro"
              target="_blank"
              rel="noopener noreferrer"
              className="uxa-submit uxa-submit--auto uxa-submit--tertiary uxa-submit--on-dark"
            >
              Get Fixes
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </motion.div>

      <AuditCheckoutPopup
        visible={showPDFUpsell}
        onClose={() => setShowPDFUpsell(false)}
        user={user}
        setUser={(u) => { setUser(u); setUserPlan(u?.plan || 'FREE'); }}
        source="report_blur"
        auditedUrl={audit?.input_data}
      />
    </div>
  );
}
